import { useState, useEffect } from 'react';
import './Orders.css';
import { orderAPI } from '../../../config/order-api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Bike, Package, Pencil, RefreshCw, User, Check, AlertCircle, ShoppingBag, History, Search, X, ChevronDown, ChevronUp, Star } from 'lucide-react';
import CustomOrderModals from '../CustomOrders/CustomOrderModals';
import { ORDER_STATUS, getStatusLabel, IS_ALFAJR } from '../../../constants/general';
import { formatAmount } from '../../../utils/utils';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import DispatchEditModal from '../DispatchHistory/DispatchEditModal/DispatchEditModal';
import { dispatchAPI } from '../../../config/dispatch-api';
import Users from '../Users/Users';
import { couponAPI } from '../../../config/coupon-api';
import { API_CONFIG } from '../../../config/api';
import { userAPI } from '../../../config/user-api';

const getOrdinalSuffix = (i) => {
    const j = i % 10,
          k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
};

const splitOrderDateTime = (value) => {
    if (!value || value === '-') {
        return { date: '-', time: '' };
    }

    const match = String(value).match(/^(\d{2}-\d{2}-\d{4})\s+(.+)$/);
    if (!match) {
        return { date: value, time: '' };
    }

    return { date: match[1], time: match[2] };
};

const EXPRESS_DELIVERY_BADGE_SRC = `${process.env.PUBLIC_URL || ''}/images/express_delivery_badge.webp`;

const shouldShowExpressDeliveryBadge = (order) => {
    if (!order) return false;
    if (order.isExpressDelivery === true) return true;
    const date = String(order.deliveryDate || '').trim();
    const noTimeSlot = !String(order.timeSlot || '').trim();
    if (!noTimeSlot) return false;
    return date === 'Today' || date === 'Express';
};

const ExpressDeliveryBadgeImage = ({ className = '' }) => (
    <img
        src={EXPRESS_DELIVERY_BADGE_SRC}
        alt="Express delivery"
        className={`orders-express-delivery-badge ${className}`.trim()}
    />
);

const Orders = ({ userFilter, adminData, refreshTrigger }) => {
    const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_ORDERS);
    const canViewLogs = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_EVENT_LOGS);
    const canCreateCustomOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_CUSTOM_ORDERS);
    const canViewOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
    const canViewCustomOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_CUSTOM_ORDERS);

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [staffActionLoading, setStaffActionLoading] = useState(false);
    const [showStaffSelectModal, setShowStaffSelectModal] = useState(false);
    const [staffAssignmentType, setStaffAssignmentType] = useState(null);
    const [staffModalMode, setStaffModalMode] = useState('assign');
    const [isEditingStatus, setIsEditingStatus] = useState(false);
    const [editedStatus, setEditedStatus] = useState("");
    const [userName, setUserName] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false,
        limit: 50
    });

    // Dispatch Integration
    const [showDispatchEditModal, setShowDispatchEditModal] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [dispatchDrivers, setDispatchDrivers] = useState([]);
    const [dispatchStoreCoords, setDispatchStoreCoords] = useState(null);
    const [dispatchLoading, setDispatchLoading] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState({ isOpen: false, status: null, title: '', message: '', color: '', icon: null });
    const [cancelReasonInput, setCancelReasonInput] = useState('');

    const [cancelRequestCount, setCancelRequestCount] = useState(0);

    // Address Selection Integration
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [customerAddresses, setCustomerAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelOrders, setCancelOrders] = useState([]);
    const [cancelOrdersLoading, setCancelOrdersLoading] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [modalSearchQuery, setModalSearchQuery] = useState('');
    const [customOrderOverlayId, setCustomOrderOverlayId] = useState(null);
    const [showCustomCreateModal, setShowCustomCreateModal] = useState(false);

    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedUserFilter, setSelectedUserFilter] = useState(null);

    const getInitialFilters = () => {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);

        let lastMonth = now.getMonth();
        let filterYear = now.getFullYear();
        if (lastMonth === 0) {
            lastMonth = 12;
            filterYear -= 1;
        }

        let defaultOrderType = 'all';
        if (canViewOrders && !canViewCustomOrders) {
            defaultOrderType = 'app';
        } else if (!canViewOrders && canViewCustomOrders) {
            defaultOrderType = 'custom';
        }

        return {
            filterType: 'today',
            date: yesterday.toISOString().split('T')[0],
            month: lastMonth.toString(),
            year: filterYear.toString(),
            startDate: '',
            endDate: '',
            userId: '',
            couponId: '',
            searchQuery: '',
            orderReference: '',
            orderStatus: 'All',
            driverId: 'All',
            timeSlot: 'All',
            orderType: defaultOrderType,
        };
    };

    const [filters, setFilters] = useState(getInitialFilters());
    const [filterOptions, setFilterOptions] = useState({
        drivers: [],
        timeSlots: []
    });
    const [stats, setStats] = useState(null);

    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [addProductForm, setAddProductForm] = useState({ productId: '', qty: 1 });
    const [addProductLoading, setAddProductLoading] = useState(false);
    const [productOptions, setProductOptions] = useState(null);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [selectedCleanType, setSelectedCleanType] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);

    const [showEditItemModal, setShowEditItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editItemQty, setEditItemQty] = useState(1);
    const [editItemLoading, setEditItemLoading] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isEditingDeliverySchedule, setIsEditingDeliverySchedule] = useState(false);
    const [deliveryDateInput, setDeliveryDateInput] = useState('');
    const [deliveryTimeSlotInput, setDeliveryTimeSlotInput] = useState('');
    const [deliveryTimeSlotIdInput, setDeliveryTimeSlotIdInput] = useState('');
    const [isExpressDeliveryInput, setIsExpressDeliveryInput] = useState(false);
    const [availableDeliverySlots, setAvailableDeliverySlots] = useState([]);
    const [loadingDeliverySlots, setLoadingDeliverySlots] = useState(false);
    const [savingDeliverySchedule, setSavingDeliverySchedule] = useState(false);
    const [isEditingBill, setIsEditingBill] = useState(false);
    const [editDeliveryCharge, setEditDeliveryCharge] = useState('');
    const [editTipAmount, setEditTipAmount] = useState('');
    const [savingBill, setSavingBill] = useState(false);

    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [isCouponDetailsOpen, setIsCouponDetailsOpen] = useState(false);
    const [couponLoading, setCouponLoading] = useState(false);
    const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [showStaffStats, setShowStaffStats] = useState(false);
    const [showDailyOvertimeModal, setShowDailyOvertimeModal] = useState(false);

    const navigate = useNavigate();

    const fetchOrders = async (page = 1, filterOverride = null, config = {}) => {
        const f = filterOverride || filters;
        try {
            if (config.orders !== false) setLoading(true);
            setError(null);
            const params = {
                page,
                limit: pagination.limit || 50,
                filterType: f.filterType,
                date: f.date,
                month: f.month,
                year: f.year,
                startDate: f.startDate,
                endDate: f.endDate,
                userId: f.userId,
                couponId: f.couponId,
                searchQuery: f.searchQuery,
                orderStatus: f.orderStatus,
                driverId: f.driverId || 'All',
                timeSlot: f.timeSlot || 'All',
                orderType: f.orderType || 'all',
                stats: config.stats !== undefined ? config.stats : (page === 1),
                orders: config.orders !== false,
                timeSlots: config.timeSlots || (filterOptions.timeSlots.length === 0),
                drivers: config.drivers || (filterOptions.drivers.length === 0),
            };
            const response = await orderAPI.getAllOrders(params);

            if (response.success) {
                if (params.orders) {
                    setOrders(response.data.orders || []);
                    setPagination(prev => ({ ...prev, ...response.data.pagination }));
                }

                if (params.stats && response.data.stats) {
                    setStats(response.data.stats);
                    setCancelRequestCount(response.data.stats.pendingCancelCount || 0);
                }

                if (response.data.drivers || response.data.timeSlots) {
                    setFilterOptions(prev => ({
                        timeSlots: response.data.timeSlots || prev.timeSlots,
                        drivers: response.data.drivers || prev.drivers
                    }));
                }
            } else {
                setError(response.msg || 'Failed to fetch orders');
                toast.error(response.msg || 'Failed to fetch orders');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            setError('Error fetching orders');
            toast.error('Error fetching orders');
        } finally {
            if (config.orders !== false) setLoading(false);
        }
    };

    const getShareStaffId = () => orderDetails?.staff?.delivery?.id || '';

    const buildStaffShareMessage = () => {
        if (!orderDetails) return '';

        const customerName = orderDetails.customer?.name
            || orderDetails.address?.receiversName
            || '';
        const orderRef = orderDetails.orderId;
        const orderMongoId = orderDetails.id;
        const assignedStaffId = getShareStaffId();
        let riderUrl = `${API_CONFIG.STAFF_RIDER_BASE_URL}/admin/rider?id=${orderMongoId}&ordersource=app`;
        if (assignedStaffId) {
            riderUrl += `&rid=${assignedStaffId}`;
        }

        const lines = [
            `ORDER #${orderRef}`,
            '',
            `To: ${customerName}`,
        ];

        const total = orderDetails.billDetails?.totalPrice || '';
        lines.push(`Order Total: QR ${total}`);

        lines.push('', riderUrl);
        return lines.join('\n');
    };

    const openWhatsAppShare = (phoneNumber, message) => {
        const encodedText = encodeURIComponent(message);
        const phone = phoneNumber ? String(phoneNumber).replace(/[^\d]/g, '') : '';
        const waUrl = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
        window.open(waUrl, '_blank');
    };

    const closeStaffSelectModal = () => {
        setShowStaffSelectModal(false);
        setStaffModalMode('assign');
    };

    const handleShareToWhatsAppOther = () => {
        const message = buildStaffShareMessage();
        openWhatsAppShare(null, message);
        closeStaffSelectModal();
    };

    const handleShareToStaff = (staff) => {
        if (!staff) return;
        const message = buildStaffShareMessage();
        openWhatsAppShare(staff.phoneNumber, message);
        closeStaffSelectModal();
    };

    const handleAssignStaff = async (staffId) => {
        if (!selectedOrderId || !staffAssignmentType) return;
        try {
            setStaffActionLoading(true);
            const response = await orderAPI.assignStaff({
                orderId: selectedOrderId,
                staffId,
                assignType: staffAssignmentType
            });
            if (response.success) {
                toast.success('Staff assigned successfully');
                closeStaffSelectModal();
                fetchOrderDetails(selectedOrderId);
                fetchOrders(pagination.currentPage);
            } else {
                toast.error(response.msg || 'Failed to assign staff');
            }
        } catch (error) {
            toast.error('Error assigning staff');
        } finally {
            setStaffActionLoading(false);
        }
    };

    const openDispatchEditModal = async (dispatchDetails, onCloseModal) => {
        if (!dispatchDetails?.routeId) return;

        onCloseModal?.();

        try {
            setDispatchLoading(true);

            const response = await dispatchAPI.getDispatchHistory(
                1, 1, 'today', null, null,
                dispatchDetails.routeId
            );

            if (response.success && response.data?.route) {
                setDispatchDrivers(response.data.drivers || []);
                setDispatchStoreCoords(response.data.storeCoordinates || null);
                setSelectedRoute(response.data.route);
                setShowDispatchEditModal(true);
            } else {
                toast.error(response.msg || "Could not find the dispatch route.");
            }
        } catch (error) {
            console.error("Error opening dispatch edit:", error);
            toast.error("Failed to load dispatch details");
        } finally {
            setDispatchLoading(false);
        }
    };

    const handleEditInDispatch = async () => {
        await openDispatchEditModal(orderDetails?.dispatchDetails, () => setSelectedOrderId(null));
    };

    const handleCustomOrderEditInDispatch = async (dispatchDetails) => {
        await openDispatchEditModal(dispatchDetails, closeCustomOrderOverlay);
    };

    const getStaffModalDrivers = (mode = staffModalMode) => (
        mode === 'assign'
            ? filterOptions.drivers.filter((driver) => driver.status !== false)
            : filterOptions.drivers
    );

    const openStaffSelect = (type) => {
        setStaffAssignmentType(type);
        setStaffModalMode('assign');
        setShowStaffSelectModal(true);
    };

    const openStaffShareModal = () => {
        setStaffModalMode('share');
        setShowStaffSelectModal(true);
    };

    const fetchOrderDetails = async (orderId) => {
        if (!orderId || orderId === 'null' || orderId === 'undefined') {
            return;
        }
        try {
            setModalLoading(true);
            const response = await orderAPI.getOrderDetails(orderId);
            if (response.success) {
                const details = response.data.orderDetails || response.data;
                setOrderDetails(details);
                setDeliveryDateInput(details.deliveryDateValue || '');
                setDeliveryTimeSlotInput(details.timeSlot || '');
                setDeliveryTimeSlotIdInput(details.timeSlotId || '');
                setAvailableDeliverySlots(details.availableTimeSlots || []);
            } else {
                toast.error(response.msg || 'Failed to fetch order details');
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            toast.error('Error fetching order details');
        } finally {
            setModalLoading(false);
        }
    };

    const handleCouponClick = async (couponId) => {
        if (!couponId) return;
        try {
            setCouponLoading(true);
            const response = await couponAPI.getAllCoupons(1, 1, '', '', '', '', '', '', '', couponId);
            if (response.success && response.data.length > 0) {
                setSelectedCoupon(response.data[0]);
                setIsCouponDetailsOpen(true);
            } else {
                toast.error('Coupon not found');
            }
        } catch (error) {
            console.error('Error fetching coupon details:', error);
            toast.error('Error fetching coupon details');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleOpenAnalytics = async (couponId) => {
        try {
            setAnalyticsLoading(true);
            setIsAnalyticsDialogOpen(true);

            const response = await couponAPI.getCouponAnalytics(couponId);

            if (response.success) {
                setAnalyticsData(response.data);
            } else {
                toast.error(response.error || 'Failed to fetch analytics');
                setIsAnalyticsDialogOpen(false);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to fetch analytics');
            setIsAnalyticsDialogOpen(false);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const getDiscountDisplay = (coupon) => {
        if (!coupon) return '';
        if (coupon.discountType === 'percentage') {
            return `${coupon.percentageValue}% Discount`;
        } else if (coupon.discountType === 'free_delivery') {
            return 'Free Delivery';
        } else if (coupon.discountType === 'percentage_free_delivery') {
            return `${coupon.percentageValue}% Discount + Free Delivery`;
        }
        return '-';
    };

    const getUserTypeDisplay = (userType, userCount) => {
        if (userType === 'all-users') return 'All Users';
        if (userType === 'selected-users') {
            if (userCount === 1) return 'Single User';
            return `Bulk Users (${userCount})`;
        }
        return userType;
    };

    // Fetch initial filter options and orders on mount
    useEffect(() => {
        const hasExternalFilter = userFilter?.userId || userFilter?.couponId || userFilter?.orderReference || userFilter?.driverId;
        if (!hasExternalFilter) {
            // Normal load: Fetch everything for 'today'
            fetchOrders(1, null, { drivers: true, timeSlots: true });
        }
    }, []);

    useEffect(() => {
        if (!refreshTrigger?.count) return;

        if (refreshTrigger.reset) {
            const defaultFilters = getInitialFilters();
            setFilters(defaultFilters);
            fetchOrders(1, defaultFilters);
        } else {
            fetchOrders(1);
        }
    }, [refreshTrigger]);

    useEffect(() => {
        if (userFilter?.userId) {
            if (userFilter.userName) {
                setUserName(userFilter.userName);
            }

            const newFilters = {
                filterType: 'all',
                date: '',
                month: '',
                year: '',
                startDate: '',
                endDate: '',
                searchQuery: '',
                userId: userFilter.userId,
                couponId: '',
                orderStatus: 'All',
                driverId: 'All',
                timeSlot: 'All',
            };
            setFilters(newFilters);
            fetchOrders(1, newFilters, { drivers: true, timeSlots: true, stats: true });
            setShowUserModal(false);
            navigate('/dashboard', { replace: true, state: { page: 'orders' } });
        } else if (userFilter?.couponId) {
            if (userFilter.couponName) {
                setUserName(userFilter.couponName);
            }

            const newFilters = {
                filterType: 'all',
                date: '',
                month: '',
                year: '',
                startDate: '',
                endDate: '',
                searchQuery: '',
                userId: '',
                couponId: userFilter.couponId,
                orderStatus: 'All',
                driverId: 'All',
                timeSlot: 'All',
            };
            setFilters(newFilters);
            fetchOrders(1, newFilters, { drivers: true, timeSlots: true, stats: true });
            setShowUserModal(false);
            navigate('/dashboard', { replace: true, state: { page: 'orders' } });
        } else if (userFilter?.orderReference) {
            const normalizedOrderReference = String(userFilter.orderReference).replace(/^#/, '').trim();
            const orderReferenceFilters = {
                filterType: 'all',
                date: '',
                month: '',
                year: '',
                startDate: '',
                endDate: '',
                searchQuery: normalizedOrderReference,
                userId: '',
                couponId: '',
                orderReference: normalizedOrderReference,
                orderStatus: 'All',
                driverId: 'All',
                timeSlot: 'All',
            };
            setFilters(orderReferenceFilters);
            fetchOrders(1, orderReferenceFilters, { drivers: true, timeSlots: true, stats: true });
            setShowUserModal(false);
            navigate('/dashboard', { replace: true, state: { page: 'orders' } });
        } else if (userFilter?.openOrderId) {
            setSelectedOrderId(userFilter.openOrderId);
            navigate('/dashboard', { replace: true, state: { page: 'orders' } });
        } else if (userFilter?.driverId) {
            const now = new Date();
            const driverFilters = {
                filterType: 'range',
                date: '',
                month: '',
                year: '',
                startDate: '2024-01-01',
                endDate: now.toISOString().split('T')[0],
                searchQuery: '',
                userId: '',
                couponId: '',
                orderStatus: 'All',
                driverId: userFilter.driverId,
                timeSlot: 'All',
            };
            setFilters(driverFilters);
            fetchOrders(1, driverFilters, { drivers: true, timeSlots: true, stats: true });
            setShowStaffStats(true); // Automatically show staff stats when a driver is filtered
            navigate('/dashboard', { replace: true, state: { page: 'orders' } });
        }

    }, [userFilter]);

    useEffect(() => {
        if (selectedOrderId) {
            fetchOrderDetails(selectedOrderId);
        }
    }, [selectedOrderId]);

    const fetchCancelOrders = async () => {
        try {
            setCancelOrdersLoading(true);
            const response = await orderAPI.getAllOrders({
                page: 1,
                limit: 200,
                hasCancelRequest: true,
                stats: false,
            });

            if (response.success) {
                setCancelOrders(response.data.orders || []);
            }
        } catch (error) {
            console.error('Error fetching cancel orders:', error);
        } finally {
            setCancelOrdersLoading(false);
        }
    };

    useEffect(() => {
        if (showCancelModal) {
            fetchCancelOrders();
        }
    }, [showCancelModal]);

    useEffect(() => {
        if (productOptions && productOptions.choice && productOptions.choice.length > 0) {
            const firstChoice = productOptions.choice[0];
            setSelectedChoice({ title: firstChoice.title, price: firstChoice.price });
        }
    }, [productOptions]);

    const handleFilterChange = (filterType, value) => {
        if (filterType === 'filterType') {
            setFilters(prev => {
                const newFilters = {
                    ...prev,
                    filterType: value,
                    timeSlot: 'All',
                    driverId: 'All'
                };

                if (value === 'range') {
                    newFilters.startDate = '';
                    newFilters.endDate = '';
                } else if (value === 'date') {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    newFilters.date = yesterday.toISOString().split('T')[0];
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                } else if (value === 'month') {
                    const now = new Date();
                    let lastMonth = now.getMonth();
                    let filterYear = now.getFullYear();

                    if (lastMonth === 0) {
                        lastMonth = 12;
                        filterYear -= 1;
                    }

                    newFilters.month = lastMonth.toString();
                    newFilters.year = filterYear.toString();
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                } else {
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                }
                return newFilters;
            });
        } else if (filterType === 'date') {
            setFilters(prev => {
                const newFilters = { ...prev, date: value, timeSlot: 'All', driverId: 'All' };
                if (value) {
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                }
                return newFilters;
            });
        } else if (filterType === 'month') {
            setFilters(prev => {
                const newFilters = { ...prev, month: value, timeSlot: 'All', driverId: 'All' };
                if (value && newFilters.year) {
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                }
                return newFilters;
            });
        } else if (filterType === 'year') {
            setFilters(prev => {
                const newFilters = { ...prev, year: value, timeSlot: 'All', driverId: 'All' };
                if (value && newFilters.month) {
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                }
                return newFilters;
            });
        }
        else if (filterType === 'startDate' || filterType === 'endDate') {
            setFilters(prev => {
                const newFilters = { ...prev, [filterType]: value };
                if (newFilters.startDate && newFilters.endDate) {
                    fetchOrders(1, newFilters, { timeSlots: true, orders: false, stats: false });
                }
                return newFilters;
            });
        } else {
            setFilters(prev => ({ ...prev, [filterType]: value }));
        }
    };

    const applyFilters = () => {
        if (filters.filterType === 'range' && (!filters.startDate || !filters.endDate)) {
            toast.error('Please select both start and end dates');
            return;
        }
        const clearedFilters = { ...filters, searchQuery: '', orderReference: '' };
        setFilters(clearedFilters);
        fetchOrders(1, clearedFilters);
    };

    const handleGlobalSearch = () => {
        if (!modalSearchQuery.trim()) {
            toast.error('Please enter a search query');
            return;
        }

        const now = new Date();
        const globalFilters = {
            ...getInitialFilters(),
            filterType: 'range',
            searchQuery: modalSearchQuery.trim(),
            date: '',
            month: '',
            year: '',
            startDate: '2021-01-01',
            endDate: now.toISOString().split('T')[0],
            orderStatus: 'All',
            driverId: 'All',
            timeSlot: 'All',
        };

        setFilters(globalFilters);
        setShowSearchModal(false);
        setShowStaffStats(false);
        setModalSearchQuery('');
        fetchOrders(1, globalFilters);
    };


    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchOrders(newPage);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed': return 'orders-status-confirmed';
            case 'cancelled': return 'orders-status-cancelled';
            case 'delivered': return 'orders-status-delivered';
            case 'pending': return 'orders-status-pending';
            default: return 'orders-status-pending';
        }
    };

    const renderStaffBadge = (name, type) => {
        if (!name) {
            return <span>-</span>;
        }

        const isPacking = type === 'packing';
        const Icon = isPacking ? Package : Bike;
        const rowClass = isPacking ? 'orders-staff-row-packing' : 'orders-staff-row-delivery';

        return (
            <div className={"orders-staff-row " + rowClass}>
                <Icon size={14} strokeWidth={2.2} />
                <span className="orders-staff-name">{name}</span>
            </div>
        );
    };

    const handleOrderClick = (order) => {
        if (order.type === 'custom') {
            setCustomOrderOverlayId(order.id);
            return;
        }
        setSelectedOrderId(order.id);
    };

    const closeCustomOrderOverlay = () => {
        setCustomOrderOverlayId(null);
        setShowCustomCreateModal(false);
    };

    const closeModal = () => {
        setSelectedOrderId(null);
        setOrderDetails(null);
        setIsEditingDeliverySchedule(false);
        setDeliveryDateInput('');
        setDeliveryTimeSlotInput('');
        setDeliveryTimeSlotIdInput('');
        setIsExpressDeliveryInput(false);
        setAvailableDeliverySlots([]);
        resetAddProductModal();
    };

    const openInMaps = (lat, long) => {
        if (!lat || !long) return;
        const url = `https://www.google.com/maps?q=${lat},${long}`;
        window.open(url, "_blank");
    };

    const resetAddProductModal = () => {
        setShowAddProductModal(false);
        setAddProductForm({ productId: '', qty: 1 });
        setProductOptions(null);
        setSelectedChoice(null);
        setSelectedCleanType(null);
        setSelectedVariant(null);
    };

    const openDeleteDialog = () => {
        setShowDeleteDialog(true);
    };

    const cancelDelete = () => {
        if (deleteLoading) return;
        setShowDeleteDialog(false);
    };

    const handleAddProductSubmit = async () => {
        if (!addProductForm.productId) {
            toast.error('Please enter a Product ID');
            return;
        }
        if (!addProductForm.qty || addProductForm.qty < 1) {
            toast.error('Quantity must be at least 1');
            return;
        }

        try {
            setAddProductLoading(true);
            const payload = {
                orderId: selectedOrderId,
                productId: addProductForm.productId,
                qty: parseInt(addProductForm.qty),
                choice: selectedChoice,
                cleanAndCutType: selectedCleanType,
                selectedVariant: selectedVariant
            };

            const response = await orderAPI.addProductToOrder(payload);

            if (response.success) {
                if (response.needsOptions) {
                    // Product has options, show options selection
                    setProductOptions(response.data.product);
                } else {
                    toast.success(response.msg || 'Product added successfully');
                    // Refresh order details
                    await fetchOrderDetails(selectedOrderId);
                    resetAddProductModal();
                }
            } else {
                toast.error(response.msg || 'Failed to add product');
            }
        } catch (error) {
            console.error('Error adding product:', error);
            toast.error('Error adding product to order');
        } finally {
            setAddProductLoading(false);
        }
    };

    const handleAddProductWithOptions = async () => {
        if (!productOptions) return;

        // Validate selections
        const hasChoices = productOptions.choice && productOptions.choice.length > 0;
        const hasCleanType = productOptions.cleanAndCutType && productOptions.cleanAndCutType.length > 0;
        const hasVariants = productOptions.variants && productOptions.variants.length > 0;

        if (hasChoices && !selectedChoice) {
            toast.error('Please select a choice option');
            return;
        }

        if (hasVariants && !selectedVariant) {
            toast.error('Please select a variant');
            return;
        }

        try {
            setAddProductLoading(true);
            const payload = {
                orderId: selectedOrderId,
                productId: parseInt(addProductForm.productId),
                qty: parseInt(addProductForm.qty),
                choice: selectedChoice,
                cleanAndCutType: selectedCleanType,
                selectedVariant: selectedVariant
            };

            const response = await orderAPI.addProductToOrder(payload);

            if (response.success && !response.needsOptions) {
                toast.success(response.msg || 'Product added successfully');
                await fetchOrderDetails(selectedOrderId);
                resetAddProductModal();
            } else {
                toast.error(response.msg || 'Failed to add product');
            }
        } catch (error) {
            console.error('Error adding product:', error);
            toast.error('Error adding product to order');
        } finally {
            setAddProductLoading(false);
        }
    };

    const handleUpdateItem = async () => {
        if (!editingItem) return;

        try {
            setEditItemLoading(true);

            const res = await orderAPI.editOrderItem({
                orderId: selectedOrderId,
                productId: editingItem.productId,
                qty: editItemQty
            });

            if (res.success) {
                await refreshOrderAfterEdit("Item updated successfully");
            } else {
                toast.error(res.msg || "Failed to update item");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to update item");
        } finally {
            setEditItemLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!editingItem) return;

        try {
            setDeleteLoading(true);

            const res = await orderAPI.editOrderItem({
                orderId: selectedOrderId,
                productId: editingItem.productId,
                qty: 0
            });

            if (res.success) {
                toast.success("Item deleted successfully");
                await fetchOrderDetails(selectedOrderId);

                setShowDeleteDialog(false);
                setShowEditItemModal(false);
            } else {
                toast.error(res.msg || "Failed to delete item");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete item");
        } finally {
            setDeleteLoading(false);
        }
    };

    const isSameAddress = (addr1, addr2) => {
        if (!addr1 || !addr2) return false;
        const id1 = addr1._id || addr1.id;
        const id2 = addr2._id || addr2.id;
        if (id1 && id2 && String(id1) === String(id2)) return true;

        return (
            String(addr1.houseNo || '') === String(addr2.houseNo || '') &&
            String(addr1.apartment || '') === String(addr2.apartment || '') &&
            String(addr1.zone || '') === String(addr2.zone || '') &&
            String(addr1.directionToReach || addr1.street || '') === String(addr2.directionToReach || addr2.street || '') &&
            String(addr1.receiversName || '') === String(addr2.receiversName || '') &&
            String(addr1.receiversPhoneNUmber || addr1.receiversPhoneNumber || addr1.receiversPhone || '') === 
            String(addr2.receiversPhoneNUmber || addr2.receiversPhoneNumber || addr2.receiversPhone || '')
        );
    };

    const executeAddressUpdate = async (address) => {
        try {
            setModalLoading(true);
            const response = await orderAPI.updateOrderAddress({
                orderId: orderDetails?.id || orderDetails?._id || selectedOrderId,
                address: address
            });
            if (response.success) {
                toast.success("Order delivery address updated successfully!");
                setShowAddressModal(false);
                if (selectedOrderId) {
                    await fetchOrderDetails(selectedOrderId);
                } else if (orderDetails?.id) {
                    await fetchOrderDetails(orderDetails.id);
                }
            } else {
                toast.error(response.msg || "Failed to update order address");
            }
        } catch (err) {
            console.error("Error updating order address:", err);
            toast.error("Failed to update order address");
        } finally {
            setModalLoading(false);
        }
    };

    const handleSelectAddress = (address) => {
        const isAlreadySet = isSameAddress(address, orderDetails?.address);
        const confirmMsg = isAlreadySet 
            ? "This address is already set on the order. Are you sure you want to update/re-apply it?"
            : "Are you sure you want to update the delivery address of this order to the selected address?";
        
        if (window.confirm(confirmMsg)) {
            executeAddressUpdate(address);
        }
    };

    const handleAddressChangeClick = async () => {
        const userId = orderDetails?.customer?.id || orderDetails?.userId;
        if (!userId) {
            toast.error("Customer ID not found for this order");
            return;
        }
        try {
            setLoadingAddresses(true);
            const response = await userAPI.getUserDetails('id', userId);
            if (response.success && response.data) {
                const addressesList = response.data.addresses || [];
                if (addressesList.length === 0) {
                    toast.error("No addresses found for this customer. Please add an address to customer profile first.");
                    return;
                }
                setCustomerAddresses(addressesList);
                setShowAddressModal(true);
            } else {
                toast.error(response.msg || "Failed to fetch customer addresses");
            }
        } catch (err) {
            console.error("Error fetching customer addresses:", err);
            toast.error("Failed to fetch customer addresses");
        } finally {
            setLoadingAddresses(false);
        }
    };

    const handleApproveCancel = async (orderId) => {
        try {
            setModalLoading(true);
            const response = await orderAPI.cancelRequestAction(orderId, 'approve');
            if (response.success) {
                toast.success(response.msg || 'Order cancelled successfully');
                setCancelRequestCount(prev => Math.max(0, prev - 1));
                await fetchOrderDetails(orderId);
                fetchOrders(1);
                fetchCancelOrders();
            } else {
                toast.error(response.msg || 'Failed to approve cancellation');
            }
        } catch (error) {
            console.error('Error approving cancellation:', error);
            toast.error('Error approving cancellation');
        } finally {
            setModalLoading(false);
        }
    };

    const handleRejectCancel = async (orderId) => {
        try {
            setModalLoading(true);
            const response = await orderAPI.cancelRequestAction(orderId, 'reject');
            if (response.success) {
                toast.success(response.msg || 'Cancellation request rejected');
                setCancelRequestCount(prev => Math.max(0, prev - 1));
                await fetchOrderDetails(orderId);
                fetchOrders(1);
                fetchCancelOrders();
            } else {
                toast.error(response.msg || 'Failed to reject cancellation');
            }
        } catch (error) {
            console.error('Error rejecting cancellation:', error);
            toast.error('Error rejecting cancellation');
        } finally {
            setModalLoading(false);
        }
    };

    const getCancelledByLabel = (cancelledBy) => {
        if (!cancelledBy) return "customer";
        const value = String(cancelledBy).toLowerCase();
        if (value === "admin") return "admin";
        if (value === "user" || value === "customer") return "customer";
        return value;
    };

    const getStatusSubtext = (status, packingStatus, cancelledBy) => {
        switch (status) {
            case ORDER_STATUS.PENDING:
                return "Customer waiting for order confirmation";
            case ORDER_STATUS.CONFIRMED:
                return packingStatus === 'packed'
                    ? "Order is packed and ready for delivery"
                    : "Order is confirmed and being packed";
            case ORDER_STATUS.ON_DELIVERY:
                return "Order is on the way for delivery";
            case ORDER_STATUS.DELIVERED:
                return "Order delivered successfully";
            case ORDER_STATUS.CANCELLED:
                return `Order cancelled by ${getCancelledByLabel(cancelledBy)}`;
            default:
                return "";
        }
    };

    const handleUpdateOrderStatus = async (newStatus, reason = null, isConfirmed = false) => {
        if (!orderDetails || !newStatus) return;

        // Custom Confirmation Logic
        if (!isConfirmed) {
            const config = {
                [ORDER_STATUS.CONFIRMED]: {
                    title: 'Confirm Order',
                    message: `Are you sure you want to mark this order as ${getStatusLabel(ORDER_STATUS.CONFIRMED)}?`,
                    color: 'blue',
                    icon: Check
                },
                [ORDER_STATUS.ON_DELIVERY]: {
                    title: 'Out for Delivery',
                    message: `Are you sure you want to set this order as ${getStatusLabel(ORDER_STATUS.ON_DELIVERY)}?`,
                    color: 'orange',
                    icon: Bike
                },
                [ORDER_STATUS.DELIVERED]: {
                    title: 'Confirm Delivery',
                    message: `Are you sure you want to mark this order as ${getStatusLabel(ORDER_STATUS.DELIVERED)}?`,
                    color: 'green',
                    icon: ShoppingBag
                },
                [ORDER_STATUS.CANCELLED]: {
                    title: 'Cancel Order',
                    message: `Are you sure you want to cancel this order? This action cannot be undone.`,
                    color: 'red',
                    icon: AlertCircle
                }
            }[newStatus];

            if (config) {
                setStatusConfirm({
                    isOpen: true,
                    status: newStatus,
                    ...config
                });
                return;
            }
        }

        try {
            setModalLoading(true);
            const response = await orderAPI.updateOrderStatus({
                orderId: orderDetails.id,
                status: newStatus,
                cancelReason: reason || cancelReasonInput
            });

            if (response.success) {
                toast.success(`Order status updated to ${getStatusLabel(newStatus)}`);
                await fetchOrderDetails(orderDetails.id);
                fetchOrders(pagination.currentPage);
                setIsEditingStatus(false);
                setStatusConfirm({ isOpen: false, status: null });
                setCancelReasonInput('');
            } else {
                toast.error(response.msg || 'Failed to update order status');
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            toast.error('Error updating order status');
        } finally {
            setModalLoading(false);
        }
    };

    const fetchSlotsForDeliveryDate = async (selectedDate) => {
        if (!selectedDate) {
            setAvailableDeliverySlots([]);
            return [];
        }

        try {
            setLoadingDeliverySlots(true);
            const response = await dispatchAPI.getDispatchDetails(selectedDate);
            if (!response.success) {
                toast.error(response.msg || 'Failed to load time slots');
                setAvailableDeliverySlots([]);
                return [];
            }

            const slots = (response.data?.timeSlots || [])
                .filter(slot => slot.enabled !== false && slot.isEnabled !== false);

            setAvailableDeliverySlots(slots);
            return slots;
        } catch (error) {
            console.error('Error fetching delivery slots:', error);
            toast.error('Error loading time slots');
            setAvailableDeliverySlots([]);
            return [];
        } finally {
            setLoadingDeliverySlots(false);
        }
    };

    const handleStartEditDeliverySchedule = async () => {
        if (!orderDetails) return;
        setIsEditingDeliverySchedule(true);
        setIsExpressDeliveryInput(orderDetails.isExpressDelivery || false);
        const selectedDate = orderDetails.deliveryDateValue || '';
        if (selectedDate) {
            await fetchSlotsForDeliveryDate(selectedDate);
        }
    };

    const handleCancelEditDeliverySchedule = () => {
        if (!orderDetails) return;
        setIsEditingDeliverySchedule(false);
        setDeliveryDateInput(orderDetails.deliveryDateValue || '');
        setDeliveryTimeSlotInput(orderDetails.timeSlot || '');
        setDeliveryTimeSlotIdInput(orderDetails.timeSlotId || '');
        setIsExpressDeliveryInput(orderDetails.isExpressDelivery || false);
    };

    const handleDeliveryTypeChange = (type) => {
        const isExpress = type === 'express';
        setIsExpressDeliveryInput(isExpress);
        if (isExpress) {
            setDeliveryTimeSlotInput('');
            setDeliveryTimeSlotIdInput('');
        }
    };

    const handleDeliveryDateChange = async (value) => {
        setDeliveryDateInput(value);
        setDeliveryTimeSlotInput('');
        setDeliveryTimeSlotIdInput('');
        await fetchSlotsForDeliveryDate(value);
    };

    const handleDeliverySlotChange = (value) => {
        setDeliveryTimeSlotInput(value);
        const selectedSlot = availableDeliverySlots.find(slot => slot.name === value);
        setDeliveryTimeSlotIdInput(selectedSlot?._id || '');
    };

    const handleSaveDeliverySchedule = async () => {
        if (!orderDetails?.id) return;
        if (!deliveryDateInput) {
            toast.error('Please select delivery date');
            return;
        }
        if (!isExpressDeliveryInput && !deliveryTimeSlotInput) {
            toast.error('Please select a time slot');
            return;
        }

        try {
            setSavingDeliverySchedule(true);
            const response = await orderAPI.updateOrderStatus({
                orderId: orderDetails.id,
                deliveryDate: deliveryDateInput,
                isExpressDelivery: isExpressDeliveryInput,
                timeSlot: isExpressDeliveryInput ? "" : deliveryTimeSlotInput,
                timeSlotId: isExpressDeliveryInput ? null : (deliveryTimeSlotIdInput || null)
            });

            if (response.success) {
                toast.success(response.msg || 'Delivery schedule updated');
                setIsEditingDeliverySchedule(false);
                await fetchOrderDetails(orderDetails.id);
                fetchOrders(pagination.currentPage);
            } else {
                toast.error(response.msg || 'Failed to update delivery schedule');
            }
        } catch (error) {
            console.error('Error updating delivery schedule:', error);
            toast.error('Error updating delivery schedule');
        } finally {
            setSavingDeliverySchedule(false);
        }
    };

    const handleSaveBill = async () => {
        if (!orderDetails?.id) return;

        if (editDeliveryCharge === '' || isNaN(editDeliveryCharge) || Number(editDeliveryCharge) < 0) {
            toast.error('Please enter a valid delivery charge');
            return;
        }

        if (!IS_ALFAJR && (editTipAmount === '' || isNaN(editTipAmount) || Number(editTipAmount) < 0)) {
            toast.error('Please enter a valid tip amount');
            return;
        }

        try {
            setSavingBill(true);
            const response = await orderAPI.updateOrderStatus({
                orderId: orderDetails.id,
                deliveryCharge: Number(editDeliveryCharge),
                tipAmount: IS_ALFAJR ? 0 : Number(editTipAmount),
                adminId: adminData?._id
            });

            if (response.success) {
                toast.success(response.msg || 'Bill details updated');
                setIsEditingBill(false);
                await fetchOrderDetails(orderDetails.id);
                fetchOrders(pagination.currentPage);
            } else {
                toast.error(response.msg || 'Failed to update bill details');
            }
        } catch (error) {
            console.error('Error updating bill details:', error);
            toast.error('Error updating bill details');
        } finally {
            setSavingBill(false);
        }
    };

    const handleStartEditBill = () => {
        setEditDeliveryCharge(orderDetails.billDetails?.deliveryCharge || 0);
        setEditTipAmount(orderDetails.billDetails?.tipAmount || 0);
        setIsEditingBill(true);
    };

    const handleCancelEditBill = () => {
        setIsEditingBill(false);
    };

    const refreshOrderAfterEdit = async (msg) => {
        toast.success(msg);
        await fetchOrderDetails(selectedOrderId);
        setShowEditItemModal(false);
    };

    const handleCustomerClick = (e, order) => {
        e.stopPropagation();

        setSelectedOrderId(null);
        setOrderDetails(null);

        const uid = order.userId != null && order.userId !== "" ? String(order.userId) : null;
        const phoneRaw = order.customerPhone != null && String(order.customerPhone).trim() !== ""
            ? String(order.customerPhone).trim()
            : "";

        if (uid) {
            setSelectedUserFilter({ userId: uid });
            setShowUserModal(true);
        } else if (phoneRaw) {
            setSelectedUserFilter({ userPhone: phoneRaw });
            setShowUserModal(true);
        } else {
            toast.error("User information not available");
        }
    };

    const handleViewLogs = () => {
        if (!orderDetails?.id) return;

        navigate('/dashboard', {
            state: {
                page: 'event-logs',
                query: orderDetails.orderId?.toString() || orderDetails.id,
                entityType: 'order',
                dateFilter: 'all_time',
                returnTo: {
                    page: 'orders',
                    openOrderId: selectedOrderId,
                    orderReference: orderDetails.orderId,
                },
            },
        });
    };

    const getWalletCouponBillInfo = (details) => {
        const bill = details?.billDetails || {};
        const couponRedeemType = bill.couponRedeemType ?? details?.couponRedeemType ?? 'direct';
        const walletCreditAmount = Number(
            bill.walletCreditAmount ?? details?.walletCreditAmount ?? bill.couponDiscount ?? 0
        );

        return {
            isWalletCoupon: couponRedeemType === 'wallet' && walletCreditAmount > 0,
            walletCreditAmount,
            couponName: bill.couponName,
            couponId: bill.couponId,
        };
    };

    const walletCouponBill = orderDetails ? getWalletCouponBillInfo(orderDetails) : null;

    return (
        <div className="orders-page">
            <div className="orders-page-header">
                <div className="orders-page-title">
                    <h1>{filters.userId ? `Orders of ${userName || 'User'}` : filters.couponId ? `Orders of Coupon ${userName || 'Coupon'}` : filters.orderReference ? `Order #${filters.orderReference}` : 'Orders'}</h1>
                    <p>View and manage app orders and custom orders</p>
                </div>

                <div className="orders-page-actions">
                    {canCreateCustomOrders && (
                        <button
                            className="orders-create-custom-btn"
                            onClick={() => setShowCustomCreateModal(true)}
                        >
                            Create Custom Order
                        </button>
                    )}
                    <button
                        className="orders-cancel-requests-btn"
                        onClick={() => setShowCancelModal(true)}
                    >
                        Cancel Requests
                        {cancelRequestCount > 0 && (
                            <span className="orders-cancel-badge">{cancelRequestCount}</span>
                        )}
                    </button>
                </div>
            </div>

            {stats && (
                <>
                    <div className="orders-stats-grid">
                        <div className="orders-stat-card">
                            <h3>Delivered Orders</h3>
                            <p className="orders-stat-value">{stats.deliveredOrders || 0}</p>
                            <p className="orders-stat-subtext">
                                App orders: {stats.appDeliveredOrders ?? 0}, Custom orders: {stats.customDeliveredOrders ?? 0}
                            </p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Item Total</h3>
                            <p className="orders-stat-value">{formatAmount(stats.totalItemTotal || 0)} QR</p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Delivery Charge</h3>
                            <p className="orders-stat-value">{formatAmount(stats.totalDeliveryCharge || 0)} QR</p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Coupon Item Discount</h3>
                            <p className="orders-stat-value">{formatAmount(stats.totalCouponItemDiscount || 0)} QR</p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Coupon Delivery Discount</h3>
                            <p className="orders-stat-value">{formatAmount(stats.totalCouponDeliveryDiscount || 0)} QR</p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Coupon Applied</h3>
                            <p className="orders-stat-value">{stats.couponAppliedCount || 0}</p>
                        </div>
                        <div className="orders-stat-card">
                            <h3>Cancelled Orders</h3>
                            <p className="orders-stat-value">{stats.cancelledOrders || 0}</p>
                        </div>
                    </div>

                    {stats.driverDetails && filters.driverId !== 'All' && (
                        <div className="orders-staff-stats-toggle-container">
                            <button
                                className="orders-staff-stats-toggle-btn-simple"
                                onClick={() => setShowStaffStats(!showStaffStats)}
                            >
                                <span>Delivery Staff Stats</span>
                                {showStaffStats ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                        </div>
                    )}
                </>
            )}

            {stats && stats.driverDetails && filters.driverId !== 'All' && showStaffStats && (
                <div className="orders-stats-grid staff-stats-container">
                    <div className="orders-stat-card staff-stats-card">
                        <h3>Delivered Orders</h3>
                        <p className="orders-stat-value">{stats.deliveredOrders || 0}</p>
                        <p className="orders-stat-subtext">
                            App orders: {stats.appDeliveredOrders ?? 0}, Custom orders: {stats.customDeliveredOrders ?? 0}
                        </p>
                    </div>
                    <div className="orders-stat-card staff-stats-card">
                        <h3>Total Collected</h3>
                        <p className="orders-stat-value">{formatAmount(stats.totalAmountDelivered || 0)} QR</p>
                    </div>
                    <div className="orders-stat-card staff-stats-card">
                        <h3>Total Tip</h3>
                        <p className="orders-stat-value">{formatAmount(stats.totalTipAmount || 0)} QR</p>
                    </div>
                    <div className="orders-stat-card staff-stats-card">
                        <h3>Commission Earned</h3>
                        <p className="orders-stat-value">
                            {formatAmount(stats.commissionEarned || 0)} QR
                        </p>
                    </div>
                    <div className="orders-stat-card staff-stats-card overtime-card">
                        <h3>Overtime Earned</h3>
                        <p className="orders-stat-value" style={{ fontSize: '26px', whiteSpace: 'nowrap' }}>
                            {stats.overtimeOrdersCount || 0} {stats.overtimeOrdersCount === 1 ? 'Order' : 'Orders'} | {formatAmount(stats.overtimeEarned || 0)} QR
                        </p>
                        {['month', 'range'].includes(filters.filterType) && (
                            <button
                                type="button"
                                className="orders-staff-stats-daily-view-btn"
                                onClick={() => setShowDailyOvertimeModal(true)}
                            >
                                Daily View <ChevronDown size={12} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!filters.userId && !filters.couponId && !filters.orderReference && (
                <div className="orders-filter-section">
                    <div className="orders-filter-form">
                        <div className="orders-filter-group">
                            <label>Filter Type</label>
                            <select
                                value={filters.filterType}
                                onChange={(e) => handleFilterChange('filterType', e.target.value)}
                                className="orders-filter-select"
                            >
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="date">Custom Date</option>
                                <option value="month">Custom Month</option>
                                <option value="range">Custom Range</option>
                            </select>
                        </div>

                        {!filters.searchQuery && filters.filterType === 'date' && (
                            <div className="orders-filter-group">
                                <label>Select Date</label>
                                <input
                                    type="date"
                                    value={filters.date}
                                    onChange={(e) => handleFilterChange('date', e.target.value)}
                                    className="orders-filter-input"
                                />
                            </div>
                        )}

                        {!filters.searchQuery && filters.filterType === 'month' && (
                            <>
                                <div className="orders-filter-group">
                                    <label>Select Month</label>
                                    <select
                                        value={filters.month}
                                        onChange={(e) => handleFilterChange('month', e.target.value)}
                                        className="orders-filter-select"
                                    >
                                        <option value="">Select Month</option>
                                        <option value="1">January</option>
                                        <option value="2">February</option>
                                        <option value="3">March</option>
                                        <option value="4">April</option>
                                        <option value="5">May</option>
                                        <option value="6">June</option>
                                        <option value="7">July</option>
                                        <option value="8">August</option>
                                        <option value="9">September</option>
                                        <option value="10">October</option>
                                        <option value="11">November</option>
                                        <option value="12">December</option>
                                    </select>
                                </div>
                                <div className="orders-filter-group">
                                    <label>Select Year</label>
                                    <select
                                        value={filters.year}
                                        onChange={(e) => handleFilterChange('year', e.target.value)}
                                        className="orders-filter-select"
                                    >
                                        <option value="">Select Year</option>
                                        {Array.from({ length: new Date().getFullYear() - 2022 + 1 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}

                        {filters.filterType === 'range' && (
                            <>
                                <div className="orders-filter-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={filters.startDate}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                        className="orders-filter-input"
                                    />
                                </div>
                                <div className="orders-filter-group">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={filters.endDate}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                        className="orders-filter-input"
                                    />
                                </div>
                            </>
                        )}

                        {canViewOrders && canViewCustomOrders && (
                            <div className="orders-filter-group">
                                <label>Order Type</label>
                                <select
                                    value={filters.orderType}
                                    onChange={(e) => handleFilterChange('orderType', e.target.value)}
                                    className="orders-filter-select"
                                >
                                    <option value="all">All</option>
                                    <option value="app">App Orders</option>
                                    <option value="custom">Custom Orders</option>
                                    {!IS_ALFAJR && <option value="web">Website Orders</option>}
                                </select>
                            </div>
                        )}

                        <div className="orders-filter-group">
                            <label>Status</label>
                            <select
                                value={filters.orderStatus}
                                onChange={(e) => handleFilterChange('orderStatus', e.target.value)}
                                className="orders-filter-select"
                            >
                                <option value="All">All Status</option>
                                {Object.values(ORDER_STATUS).map((status) => (
                                    <option key={status} value={status}>
                                        {getStatusLabel(status)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="orders-filter-group">
                            <label>Delivery Staff</label>
                            <select
                                value={filters.driverId}
                                onChange={(e) => handleFilterChange('driverId', e.target.value)}
                                className="orders-filter-select"
                            >
                                <option value="All">All Staff</option>
                                {filterOptions.drivers.map((driver) => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="orders-filter-group">
                            <label>Time Slot</label>
                            <select
                                value={filters.timeSlot}
                                onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
                                className="orders-filter-select"
                            >
                                <option value="All">All</option>
                                {[...filterOptions.timeSlots].sort((a, b) => {
                                    const getMinutes = (slotLabel) => {
                                        const label = typeof slotLabel === 'object' ? slotLabel?.name || slotLabel?._id : slotLabel;
                                        const startTime = String(label || "").split('-')[0]?.trim();
                                        const match = startTime.match(/^(\d{1,2})(?:[.:](\d{1,2}))?\s*(AM|PM)$/i);
                                        if (!match) return Number.MAX_SAFE_INTEGER;

                                        let hours = Number(match[1]);
                                        const minutes = Number(match[2] || 0);
                                        const meridiem = match[3].toUpperCase();

                                        if (meridiem === 'PM' && hours !== 12) hours += 12;
                                        if (meridiem === 'AM' && hours === 12) hours = 0;

                                        return (hours * 60) + minutes;
                                    };
                                    return getMinutes(a) - getMinutes(b);
                                }).map((slot) => (
                                    <option key={slot._id || slot.name || slot} value={slot._id || slot.name || slot}>
                                        {slot.name || slot}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="orders-filter-group">
                            <label>Search</label>
                            <button
                                className="orders-search-trigger-button"
                                onClick={() => setShowSearchModal(true)}
                            >
                                <Search size={16} />
                                Find Order
                            </button>
                        </div>

                        <button onClick={applyFilters} className="orders-apply-filters-button">
                            Apply Filters
                        </button>
                    </div>
                </div>
            )}

            <div className="orders-table-container">
                {loading ? (
                    <div className="orders-loading-state">
                        <div className="orders-loading-spinner"></div>
                        <p>Loading orders...</p>
                    </div>
                ) : error ? (
                    <div className="orders-error-state">
                        <p>{error}</p>
                        <button onClick={() => fetchOrders(pagination.currentPage)} className="orders-retry-button">
                            <RefreshCw size={16} />
                            Retry
                        </button>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="orders-empty-state">
                        <p>No orders found</p>
                    </div>
                ) : (
                    <>
                        <div className="orders-table-wrapper">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Order Id</th>
                                        <th>Type</th>
                                        <th>Order Date</th>
                                        <th>Delivery Date</th>
                                        <th>Delivered At</th>
                                        <th>Customer</th>
                                        <th>Packing Staff</th>
                                        <th>Delivery Staff</th>
                                        <th>Items</th>
                                        <th>Item Total</th>
                                        <th>All Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => {
                                        const orderDateParts = splitOrderDateTime(order.createdAt);
                                        return (
                                        <tr key={order.id}>
                                            <td>
                                                <span className={`orders-status-badge ${getStatusBadgeClass(order.status)}`}>
                                                    {order.status || "-"}
                                                </span>
                                            </td>
                                            <td className="orders-order-id-cell">
                                                <span
                                                    className="orders-order-id-link"
                                                    onClick={() => handleOrderClick(order)}
                                                >
                                                    {order.orderId || ""}
                                                </span>
                                            </td>
                                            <td className="orders-type-cell">
                                                <span className={`orders-type-text orders-type-${order.type || 'app'}`}>
                                                    {order.type === 'custom' ? 'Custom Order' : order.type === 'web' ? 'Website Order' : 'App Order'}
                                                </span>
                                            </td>
                                            <td className="orders-order-date-cell">
                                                <div className="orders-table-primary-line">
                                                    {orderDateParts.date}
                                                </div>
                                                {orderDateParts.time && (
                                                    <div className="orders-table-secondary-line">
                                                        {orderDateParts.time}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="orders-delivery-date-cell">
                                                <div className="orders-delivery-date-line">
                                                    {order.deliveryDate || ""}
                                                </div>
                                                {order.timeSlot && (
                                                    <div className="orders-table-secondary-line orders-timeslot-line">
                                                        {order.timeSlot}
                                                    </div>
                                                )}
                                                {shouldShowExpressDeliveryBadge(order) && (
                                                    <ExpressDeliveryBadgeImage className="orders-express-delivery-badge--table" />
                                                )}
                                            </td>
                                            <td className="orders-delivery-date-cell">
                                                <div className="orders-delivery-date-line">
                                                    {order.deliveredAtLabel || "-"}
                                                </div>
                                                {order.deliveredAtTime && (
                                                    <div className="orders-table-secondary-line">
                                                        {order.deliveredAtTime}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{order.customer || "-"}</td>
                                            <td>{renderStaffBadge(order.staff?.packing, 'packing')}</td>
                                            <td>{renderStaffBadge(order.staff?.delivery, 'delivery')}</td>
                                            <td>{order.items || "-"}</td>
                                            <td className="orders-price-cell">{order.itemTotal ? `${order.itemTotal} QR` : "-"}</td>
                                            <td className="orders-price-cell">{order.allTotal ? `${order.allTotal} QR` : "-"}</td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="orders-pagination">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={!pagination.hasPrev || loading}
                                    className="orders-pagination-button"
                                >
                                    Previous
                                </button>
                                <span className="orders-pagination-info">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={!pagination.hasNext || loading}
                                    className="orders-pagination-button"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Cancel Requests Modal */}
            {showCancelModal && (
                <div className="orders-modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="orders-modal-content orders-cancel-modal" onClick={e => e.stopPropagation()}>
                        <div className="orders-modal-header">
                            <h2>Pending Cancellation Requests</h2>
                            <button className="orders-close-button" onClick={() => setShowCancelModal(false)}>×</button>
                        </div>
                        <div className="orders-modal-body">
                            {cancelOrdersLoading ? (
                                <div className="orders-loading-state">
                                    <div className="orders-loading-spinner"></div>
                                    <p>Loading requests...</p>
                                </div>
                            ) : cancelOrders.length === 0 ? (
                                <div className="orders-empty-state">
                                    <p>No pending cancellation requests found.</p>
                                </div>
                            ) : (
                                <div className="orders-cancel-list">
                                    {cancelOrders.map(order => (
                                        <div
                                            key={order.id}
                                            className="orders-cancel-list-item"
                                            onClick={() => {
                                                setShowCancelModal(false);
                                                if (order.type === 'custom') {
                                                    setCustomOrderOverlayId(order.id);
                                                } else {
                                                    setSelectedOrderId(order.id);
                                                }
                                            }}
                                        >
                                            <div className="orders-cancel-item-info">
                                                <span className="orders-cancel-item-id">Order ID: #{order.orderId}</span>
                                                <span className="orders-cancel-item-customer">
                                                    {order.type === 'custom' ? 'Custom Order' : order.type === 'web' ? 'Website Order' : 'App Order'} · {order.customer}
                                                </span>
                                            </div>
                                            <div className="orders-cancel-item-meta">
                                                <span className="orders-cancel-item-status">{order.status}</span>
                                                <span className="orders-cancel-item-arrow">→</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {selectedOrderId && (
                <div className="orders-modal-overlay orders-details-modal-overlay" onClick={() => {
                    if (!modalLoading) closeModal();
                }}>
                    <div className="orders-modal-content" onClick={(e) => e.stopPropagation()}>
                        {modalLoading ? (
                            <div className="orders-loading-state">
                                <div className="orders-loading-spinner"></div>
                                <p>Loading order Details...</p>
                            </div>
                        ) : orderDetails ? (
                            <>
                                <div className="orders-modal-header">
                                    <div className="orders-modal-header-left">
                                        <h2>Order Details {orderDetails.orderId ? `- #${orderDetails.orderId}` : ""}</h2>
                                        <span className={`orders-type-badge orders-type-${orderDetails.type || 'app'}`}>
                                            {orderDetails.type === 'custom' ? 'Custom Order' : orderDetails.type === 'web' ? 'Website Order' : 'App Order'}
                                        </span>
                                    </div>
                                    <div className="orders-modal-header-actions">
                                        {canViewLogs && (
                                            <button
                                                className="orders-view-logs-header-btn"
                                                onClick={handleViewLogs}
                                                title="View Order Logs"
                                            >
                                                <History size={16} />
                                                <span>View Logs</span>
                                            </button>
                                        )}
                                        <button className="orders-close-button" onClick={closeModal}>×</button>
                                    </div>
                                </div>

                                <div className="orders-modal-body">
                                    <div className="orders-card orders-full-width orders-status-card-simple">
                                        <div className="orders-order-status-card-header">
                                            <h3 className="orders-order-status-card-title">Order Status</h3>
                                            {!isEditingStatus && canEdit && (
                                                <button
                                                    className="orders-edit-toggle-btn"
                                                    onClick={() => {
                                                        setEditedStatus(orderDetails.orderStatus);
                                                        setIsEditingStatus(true);
                                                    }}
                                                >
                                                    <Pencil size={14} className="orders-edit-toggle-icon" />
                                                    <span className="orders-edit-toggle-label-full">Edit order status</span>
                                                    <span className="orders-edit-toggle-label-mobile">Edit</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className="orders-order-status-card-body">
                                            {isEditingStatus ? (
                                                <div className="orders-order-status-edit-container">
                                                    <div className="orders-order-status-select-wrapper">
                                                        <label className="orders-order-status-input-label">Select status to update</label>
                                                        <select
                                                            className="orders-order-status-native-select"
                                                            value={editedStatus}
                                                            onChange={(e) => setEditedStatus(e.target.value)}
                                                        >
                                                            {Object.values(ORDER_STATUS).map(status => (
                                                                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    {editedStatus === ORDER_STATUS.CANCELLED && (
                                                        <div className="orders-order-status-cancel-reason-inline">
                                                            <label className="orders-order-status-input-label">Cancellation Reason (Optional)</label>
                                                            <textarea
                                                                placeholder="Enter the reason for cancellation"
                                                                value={cancelReasonInput}
                                                                onChange={(e) => setCancelReasonInput(e.target.value)}
                                                                className="orders-status-cancel-reason-textarea"
                                                                disabled={modalLoading}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="orders-order-status-edit-footer">
                                                        <button
                                                            className="orders-btn-save"
                                                            onClick={() => handleUpdateOrderStatus(editedStatus, cancelReasonInput, true)}
                                                            disabled={modalLoading}
                                                        >
                                                            {modalLoading ? "Saving..." : "Save"}
                                                        </button>
                                                        <button
                                                            className="orders-btn-cancel"
                                                            onClick={() => {
                                                                setIsEditingStatus(false);
                                                                setCancelReasonInput('');
                                                            }}
                                                            disabled={modalLoading}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="orders-order-status-info-container">
                                                    <div className="orders-order-status-content-stack">
                                                        <div className="orders-order-status-main-display">
                                                            <span className={`orders-status-pill ${orderDetails.orderStatus?.toLowerCase().replace(' ', '-')}`}>
                                                                {orderDetails.orderStatus}
                                                            </span>
                                                        </div>
                                                        <div className="orders-order-status-description">
                                                            <p className="orders-order-status-sub-desc">
                                                                {getStatusSubtext(orderDetails.orderStatus, orderDetails.packingStatus, orderDetails.cancelledBy)}
                                                            </p>
                                                            {shouldShowExpressDeliveryBadge(orderDetails) && (
                                                                <ExpressDeliveryBadgeImage className="orders-express-delivery-badge--status" />
                                                            )}
                                                            {orderDetails.orderStatus === ORDER_STATUS.CANCELLED && orderDetails.cancelReason && (
                                                                <div className="orders-order-status-cancel-reason-simple">
                                                                    <strong>Reason:</strong> {orderDetails.cancelReason}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="orders-order-status-interaction">
                                                        {canEdit && orderDetails.orderStatus === ORDER_STATUS.PENDING && (
                                                            <button
                                                                className="orders-action-stepper-btn confirm"
                                                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.CONFIRMED)}
                                                                disabled={modalLoading}
                                                            >
                                                                Confirm Order
                                                            </button>
                                                        )}
                                                        {canEdit && orderDetails.orderStatus === ORDER_STATUS.CONFIRMED && (
                                                            <button
                                                                className="orders-action-stepper-btn primary"
                                                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.ON_DELIVERY)}
                                                                disabled={modalLoading}
                                                            >
                                                                Set Out For Delivery
                                                            </button>
                                                        )}
                                                        {canEdit && orderDetails.orderStatus === ORDER_STATUS.ON_DELIVERY && (
                                                            <button
                                                                className="orders-action-stepper-btn success"
                                                                onClick={() => handleUpdateOrderStatus(ORDER_STATUS.DELIVERED)}
                                                                disabled={modalLoading}
                                                            >
                                                                Set Delivered
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {orderDetails.orderNote && (
                                        <div className="orders-card orders-full-width orders-order-note-card">
                                            <h3 className="orders-order-note-title">Order Note</h3>
                                            <p className="orders-order-note-text">{orderDetails.orderNote}</p>
                                        </div>
                                    )}

                                    {orderDetails.cancelRequest && (
                                        <div className="orders-card orders-full-width orders-cancel-request-card">
                                            <h3 className="orders-card-title">Cancel Request</h3>
                                            <div className="orders-cancel-request-content">
                                                <p>Requested by: <strong>{orderDetails.cancelRequest.staffName || orderDetails.cancelRequest.requestedBy}</strong></p>
                                                <p>Time: <strong>{orderDetails.cancelRequest.requestedAt}</strong></p>
                                                {orderDetails.cancelRequest.reason && (
                                                    <p>Reason: <strong style={{ color: '#dc2626' }}>{orderDetails.cancelRequest.reason}</strong></p>
                                                )}

                                                <div className="orders-cancel-request-actions">
                                                    <button
                                                        className="orders-approve-cancel-btn"
                                                        onClick={() => handleApproveCancel(orderDetails.id)}
                                                        disabled={modalLoading}
                                                    >
                                                        Approve & Cancel order
                                                    </button>
                                                    <button
                                                        className="orders-reject-cancel-btn"
                                                        onClick={() => handleRejectCancel(orderDetails.id)}
                                                        disabled={modalLoading}
                                                    >
                                                        Reject this request
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="orders-card orders-half-width">
                                        <h3 className="orders-card-title">Customer Details</h3>

                                        <div className="orders-row">
                                            <span className="orders-label">Name</span>
                                            <span className="orders-value">
                                                <span
                                                    className="orders-customer-link"
                                                    onClick={(e) => handleCustomerClick(e, {
                                                        userId: orderDetails.customer?.id,
                                                        customerPhone: orderDetails.customer?.phoneNumber || orderDetails.address?.receiversPhone,
                                                    })}
                                                >
                                                    {orderDetails.customer?.name || "-"}
                                                </span>
                                                {orderDetails.deliveredOrderNumber > 0 && (
                                                    <span className="orders-customer-count-badge">
                                                        {getOrdinalSuffix(orderDetails.deliveredOrderNumber)} Order
                                                    </span>
                                                )}
                                            </span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Email</span>
                                            <span className="orders-value">{orderDetails.customer?.email || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Phone</span>
                                            <span className="orders-value">{orderDetails.customer?.phoneNumber || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Since</span>
                                            <span className="orders-value">
                                                {orderDetails.customer?.since || "-"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="orders-card orders-half-width">
                                        <div className="orders-delivery-schedule-header">
                                            <h3 className="orders-card-title" style={{ margin: 0 }}>Customer Address</h3>
                                            {canEdit && (
                                                <button
                                                    className="orders-edit-toggle-btn"
                                                    onClick={handleAddressChangeClick}
                                                    disabled={loadingAddresses}
                                                >
                                                    {loadingAddresses ? (
                                                        <RefreshCw className="animate-spin" size={14} />
                                                    ) : (
                                                        <Pencil size={14} />
                                                    )}{" "}
                                                    Change
                                                </button>
                                            )}
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Zone</span>
                                            <span className="orders-value">{orderDetails.address?.zone || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">House/Aprtmnt</span>
                                            <span className="orders-value">{orderDetails.address?.houseNo || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Street</span>
                                            <span className="orders-value">{orderDetails.address?.street || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Receiver Name</span>
                                            <span className="orders-value">{orderDetails.address?.receiversName || "-"}</span>
                                        </div>

                                        <div className="orders-row">
                                            <span className="orders-label">Receiver Phone</span>
                                            <span className="orders-value">{orderDetails.address?.receiversPhone || "-"}</span>
                                        </div>

                                    </div>

                                    {orderDetails.rating && (
                                        <div className="orders-card orders-half-width">
                                            <h3 className="orders-card-title">Order Review</h3>
                                            <div className="orders-rating-details">
                                                <div className="orders-rating-stars">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            size={20}
                                                            className={i < orderDetails.rating.stars ? "star-filled" : "star-empty"}
                                                        />
                                                    ))}
                                                </div>
                                                {orderDetails.rating.reviewText && (
                                                    <div className="orders-rating-comment">
                                                        "{orderDetails.rating.reviewText}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="orders-card orders-half-width">
                                        <div className="orders-delivery-schedule-header">
                                            <h3 className="orders-card-title">Delivery Details</h3>
                                            {!isEditingDeliverySchedule && canEdit && (
                                                <button
                                                    className="orders-edit-toggle-btn"
                                                    onClick={handleStartEditDeliverySchedule}
                                                >
                                                    <Pencil size={14} /> Edit
                                                </button>
                                            )}
                                        </div>

                                        {isEditingDeliverySchedule ? (
                                            <div className="orders-delivery-schedule-edit">
                                                <div className="orders-delivery-schedule-field">
                                                    <label className="orders-order-status-input-label">Delivery Type</label>
                                                    <div className="orders-delivery-type-buttons">
                                                        <button
                                                            type="button"
                                                            className={`orders-type-btn ${!isExpressDeliveryInput ? 'active' : ''}`}
                                                            onClick={() => handleDeliveryTypeChange('timeslot')}
                                                            disabled={savingDeliverySchedule}
                                                        >
                                                            Time Slot
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`orders-type-btn ${isExpressDeliveryInput ? 'active' : ''}`}
                                                            onClick={() => handleDeliveryTypeChange('express')}
                                                            disabled={savingDeliverySchedule}
                                                        >
                                                            Express
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="orders-delivery-schedule-field">
                                                    <label className="orders-order-status-input-label">Delivery Date</label>
                                                    <input
                                                        type="date"
                                                        value={deliveryDateInput}
                                                        onChange={(e) => handleDeliveryDateChange(e.target.value)}
                                                        className="orders-delivery-date-input"
                                                        min={new Date().toISOString().split('T')[0]}
                                                        disabled={savingDeliverySchedule}
                                                    />
                                                </div>

                                                {!isExpressDeliveryInput && (
                                                    <div className="orders-delivery-schedule-field">
                                                        <label className="orders-order-status-input-label">Time Slot</label>
                                                        <select
                                                            value={deliveryTimeSlotInput}
                                                            onChange={(e) => handleDeliverySlotChange(e.target.value)}
                                                            className="orders-delivery-slot-select"
                                                            disabled={savingDeliverySchedule || loadingDeliverySlots || !deliveryDateInput}
                                                        >
                                                            <option value="">{loadingDeliverySlots ? "Loading slots..." : "Select time slot"}</option>
                                                            {availableDeliverySlots.map(slot => (
                                                                <option key={slot._id || slot.name} value={slot.name}>
                                                                    {slot.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                <div className="orders-delivery-schedule-actions">
                                                    <button
                                                        className="orders-btn-save"
                                                        onClick={handleSaveDeliverySchedule}
                                                        disabled={savingDeliverySchedule || (!isExpressDeliveryInput && loadingDeliverySlots)}
                                                    >
                                                        {savingDeliverySchedule ? "Saving..." : "Save"}
                                                    </button>
                                                    <button
                                                        className="orders-btn-cancel"
                                                        onClick={handleCancelEditDeliverySchedule}
                                                        disabled={savingDeliverySchedule}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="orders-row">
                                                    <span className="orders-label">Delivery Date</span>
                                                    <span className="orders-value">{orderDetails.deliveryDate || "-"}</span>
                                                </div>
                                                {orderDetails.isExpressDelivery ? (
                                                    <div className="orders-row orders-row-express-badge">
                                                        <span className="orders-label">Delivery</span>
                                                        <ExpressDeliveryBadgeImage className="orders-express-delivery-badge--detail" />
                                                    </div>
                                                ) : (
                                                    <div className="orders-row">
                                                        <span className="orders-label">Time Slot</span>
                                                        <span className="orders-value">{orderDetails.timeSlot || "-"}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="orders-details-grid-section">
                                        <div className="orders-card">
                                            <h3 className="orders-card-title">Staff Details</h3>

                                            <div className="orders-staff-assignment-group">
                                                <div className="orders-staff-assignment-item">
                                                    <span className="orders-label">Packing Staff</span>
                                                    {orderDetails.staff?.packing?.name ? (
                                                        <div className="orders-staff-assigned">
                                                            <div className="orders-staff-info">
                                                                <span className="orders-value">{orderDetails.staff.packing.name}</span>
                                                                <span className="orders-staff-phone">{orderDetails.staff.packing.phoneNumber}</span>
                                                            </div>
                                                            {orderDetails.orderStatus !== 'delivered' && orderDetails.orderStatus !== 'cancelled' && (
                                                                <button
                                                                    className="orders-staff-replace-btn"
                                                                    onClick={() => openStaffSelect('packing')}
                                                                >
                                                                    Replace
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (orderDetails.orderStatus === 'pending' || orderDetails.orderStatus === 'confirmed' || orderDetails.orderStatus === 'processing') ? (
                                                        <button
                                                            className="orders-staff-assign-btn"
                                                            onClick={() => openStaffSelect('packing')}
                                                        >
                                                            Assign Packing Staff
                                                        </button>
                                                    ) : (
                                                        <span className="orders-staff-unassigned">No staff assigned</span>
                                                    )}
                                                </div>

                                                <div className="orders-staff-assignment-item">
                                                    <span className="orders-label">Delivery Staff</span>
                                                    {orderDetails.staff?.delivery?.name ? (
                                                        <div className="orders-staff-assigned">
                                                            <div className="orders-staff-info">
                                                                <span className="orders-value">{orderDetails.staff.delivery.name}</span>
                                                                <span className="orders-staff-phone">{orderDetails.staff.delivery.phoneNumber}</span>
                                                            </div>
                                                            {orderDetails.orderStatus !== 'delivered' && orderDetails.orderStatus !== 'cancelled' && !orderDetails.dispatchDetails && (
                                                                <button
                                                                    className="orders-staff-replace-btn"
                                                                    onClick={() => openStaffSelect('delivery')}
                                                                >
                                                                    Replace
                                                                </button>
                                                            )}
                                                            {orderDetails.dispatchDetails && orderDetails.orderStatus !== 'delivered' && orderDetails.orderStatus !== 'cancelled' && (
                                                                <button
                                                                    className="orders-staff-dispatch-btn"
                                                                    onClick={handleEditInDispatch}
                                                                    disabled={dispatchLoading}
                                                                >
                                                                    {dispatchLoading ? 'Loading...' : 'Edit in Dispatch'}
                                                                </button>
                                                            )}
                                                            {dispatchLoading && (
                                                                <div className="orders-dispatch-loading">
                                                                    <RefreshCw className="animate-spin" size={16} />
                                                                    <span>Fetching route...</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (orderDetails.orderStatus === 'pending' || orderDetails.orderStatus === 'confirmed' || orderDetails.orderStatus === 'processing') ? (
                                                        <button
                                                            className="orders-staff-assign-btn"
                                                            onClick={() => openStaffSelect('delivery')}
                                                        >
                                                            Assign Delivery Staff
                                                        </button>
                                                    ) : (
                                                        <span className="orders-staff-unassigned">No staff assigned</span>
                                                    )}
                                                </div>
                                            </div>

                                            {orderDetails.orderStatus !== 'cancelled' && (
                                                <button
                                                    type="button"
                                                    className="orders-staff-share-btn"
                                                    onClick={openStaffShareModal}
                                                >
                                                    Share via WhatsApp
                                                </button>
                                            )}
                                        </div>

                                        {orderDetails?.timeline && orderDetails.timeline.length > 0 && (
                                            <div className="orders-card">
                                                <h3 className="orders-card-title">Order Timeline</h3>
                                                <div className="orders-timeline">
                                                    {orderDetails.timeline.map((item, index) => (
                                                        <div key={index} className="orders-timeline-item">
                                                            <div className="orders-timeline-marker">
                                                                <div className="orders-timeline-dot"></div>
                                                                {index !== orderDetails.timeline.length - 1 && (
                                                                    <div className="orders-timeline-line"></div>
                                                                )}
                                                            </div>
                                                            <div className="orders-timeline-content">
                                                                <div className="orders-timeline-label">{item.label}</div>
                                                                <div className="orders-timeline-time">{item.time}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="orders-card orders-full-width">
                                        <div className="orders-card-header">
                                            <h3 className="orders-card-title">Order Items</h3>
                                            {canEdit && (
                                                <button
                                                    className="orders-add-product-btn"
                                                    onClick={() => setShowAddProductModal(true)}
                                                >
                                                    Add
                                                </button>
                                            )}
                                        </div>

                                        {orderDetails.products?.map(item => (
                                            <div key={item.id} className="orders-item-row">
                                                <img src={item.image} className="orders-item-img" alt="" />

                                                <div className="orders-item-id">{item.productId}</div>

                                                <div className="orders-item-info">
                                                    <div className="orders-item-name">{item.name}</div>
                                                    <div className="orders-item-qty">
                                                        {item.quantity} × {item.price} QR
                                                    </div>
                                                </div>

                                                <div className="orders-item-total">{item.total} QR</div>

                                                {canEdit && (
                                                    <button
                                                        className="orders-edit-item-btn"
                                                        onClick={() => {
                                                            setEditingItem(item);
                                                            setEditItemQty(item.quantity);
                                                            setShowEditItemModal(true);
                                                        }}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="orders-card orders-half-width orders-bill-details-card">
                                        <div className="orders-delivery-schedule-header">
                                            <h3 className="orders-card-title">Bill Summary</h3>
                                            {!isEditingBill && canEdit && (
                                                <button
                                                    className="orders-edit-toggle-btn"
                                                    onClick={handleStartEditBill}
                                                >
                                                    <Pencil size={14} /> Edit
                                                </button>
                                            )}
                                        </div>

                                        <div className="orders-bill-row">
                                            <span className="orders-bill-label">Item Total</span>
                                            <span className="orders-bill-value">{orderDetails.billDetails?.itemTotal} QR</span>
                                        </div>

                                        <div className="orders-bill-row">
                                            <span className="orders-bill-label">
                                                {orderDetails.isExpressDelivery ? 'Express Delivery Charge' : 'Delivery Charge'}
                                            </span>
                                            {isEditingBill ? (
                                                <div className="orders-bill-edit-input-wrapper">
                                                    <input
                                                        type="number"
                                                        value={editDeliveryCharge}
                                                        onChange={(e) => setEditDeliveryCharge(e.target.value)}
                                                        className="orders-bill-edit-input"
                                                        onWheel={(e) => e.target.blur()}
                                                        disabled={savingBill}
                                                    />
                                                    <span className="orders-bill-unit">QR</span>
                                                </div>
                                            ) : (
                                                <span className="orders-bill-value">
                                                    {Number(orderDetails.billDetails?.deliveryCharge) > 0
                                                        ? `${orderDetails.billDetails.deliveryCharge} QR`
                                                        : (orderDetails.billDetails?.hasFreeDelivery ? 'FREE' : '0 QR')}
                                                </span>
                                            )}
                                        </div>

                                        {!isEditingBill && Number(orderDetails.billDetails?.deliveryDiscount) > 0 && (
                                            <div className="orders-bill-row orders-bill-discount">
                                                <span className="orders-bill-label" style={{ color: '#dc2626' }}>
                                                    Delivery Discount
                                                </span>
                                                <span className="orders-bill-value" style={{ color: '#dc2626' }}>
                                                    - {orderDetails.billDetails.deliveryDiscount} QR
                                                </span>
                                            </div>
                                        )}

                                        {orderDetails.billDetails?.coinDiscount > 0 && (
                                            <div className="orders-bill-row orders-bill-discount">
                                                <span className="orders-bill-label" style={{ color: '#dc2626' }}>
                                                    🪙 Coin Discount
                                                </span>
                                                <span className="orders-bill-value" style={{ color: '#dc2626' }}>- {orderDetails.billDetails?.coinDiscount} QR</span>
                                            </div>
                                        )}

                                        {orderDetails.billDetails?.walletDiscount > 0 && (
                                            <div className="orders-bill-row orders-bill-discount">
                                                <span className="orders-bill-label" style={{ color: '#dc2626' }}>
                                                    Paying from Wallet
                                                </span>
                                                <span className="orders-bill-value" style={{ color: '#dc2626' }}>- {orderDetails.billDetails?.walletDiscount} QR</span>
                                            </div>
                                        )}

                                        {walletCouponBill?.isWalletCoupon && (
                                            <div className="orders-bill-row orders-bill-wallet-credit">
                                                <div className="orders-bill-label-stack">
                                                    <span className="orders-bill-label" style={{ color: '#059669' }}>
                                                        💰 Wallet credit (on delivery)
                                                    </span>
                                                    {walletCouponBill.couponName && (
                                                        <div className="orders-coupon-applied-wrapper">
                                                            <span
                                                                className="orders-coupon-link-sub"
                                                                onClick={() => handleCouponClick(walletCouponBill.couponId)}
                                                            >
                                                                {walletCouponBill.couponName}
                                                            </span>
                                                            <span className="orders-applied-text"> Applied</span>
                                                        </div>
                                                    )}
                                                    {orderDetails.orderStatus === ORDER_STATUS.DELIVERED && (
                                                        <span className="orders-wallet-credited-badge">Wallet credited</span>
                                                    )}
                                                </div>
                                                <span className="orders-bill-value" style={{ color: '#059669' }}>
                                                    {walletCouponBill.walletCreditAmount} QR
                                                </span>
                                            </div>
                                        )}

                                        {!walletCouponBill?.isWalletCoupon && orderDetails.billDetails?.couponItemDiscount > 0 && (
                                            <div className="orders-bill-row orders-bill-discount">
                                                <div className="orders-bill-label-stack">
                                                    <span className="orders-bill-label" style={{ color: '#dc2626' }}>
                                                        🎁 Coupon Item Discount
                                                    </span>
                                                    {orderDetails.billDetails?.couponName && (
                                                        <div className="orders-coupon-applied-wrapper">
                                                            <span
                                                                className="orders-coupon-link-sub"
                                                                onClick={() => handleCouponClick(orderDetails.billDetails.couponId)}
                                                            >
                                                                {orderDetails.billDetails.couponName}
                                                            </span>
                                                            <span className="orders-applied-text"> Applied</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="orders-bill-value" style={{ color: '#dc2626' }}>- {orderDetails.billDetails?.couponItemDiscount} QR</span>
                                            </div>
                                        )}

                                        {!walletCouponBill?.isWalletCoupon && orderDetails.billDetails?.couponDeliveryDiscount > 0 && (
                                            <div className="orders-bill-row orders-bill-discount">
                                                <div className="orders-bill-label-stack">
                                                    <span className="orders-bill-label" style={{ color: '#dc2626' }}>
                                                        🎁 Coupon Delivery Discount
                                                    </span>
                                                    {orderDetails.billDetails?.couponName && (
                                                        <div className="orders-coupon-applied-wrapper">
                                                            <span
                                                                className="orders-coupon-link-sub"
                                                                onClick={() => handleCouponClick(orderDetails.billDetails.couponId)}
                                                            >
                                                                {orderDetails.billDetails.couponName}
                                                            </span>
                                                            <span className="orders-applied-text"> Applied</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="orders-bill-value" style={{ color: '#dc2626' }}>- {orderDetails.billDetails?.couponDeliveryDiscount} QR</span>
                                            </div>
                                        )}

                                        {!IS_ALFAJR && (orderDetails.billDetails?.tipAmount !== undefined || isEditingBill) && (
                                            <div className="orders-bill-row">
                                                <span className="orders-bill-label">Tip Amount</span>
                                                {isEditingBill ? (
                                                    <div className="orders-bill-edit-input-wrapper">
                                                        <input
                                                            type="number"
                                                            value={editTipAmount}
                                                            onChange={(e) => setEditTipAmount(e.target.value)}
                                                            className="orders-bill-edit-input"
                                                            onWheel={(e) => e.target.blur()}
                                                            disabled={savingBill}
                                                        />
                                                        <span className="orders-bill-unit">QR</span>
                                                    </div>
                                                ) : (
                                                    <span className="orders-bill-value">{orderDetails.billDetails?.tipAmount || 0} QR</span>
                                                )}
                                            </div>
                                        )}

                                        {isEditingBill && (
                                            <div className="orders-delivery-schedule-actions" style={{ marginTop: '15px' }}>
                                                <button
                                                    className="orders-btn-save"
                                                    onClick={handleSaveBill}
                                                    disabled={savingBill}
                                                >
                                                    {savingBill ? "Saving..." : "Save"}
                                                </button>
                                                <button
                                                    className="orders-btn-cancel"
                                                    onClick={handleCancelEditBill}
                                                    disabled={savingBill}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}

                                        <div className="orders-bill-row orders-bill-total" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                                            <span className="orders-bill-label" style={{ fontWeight: 'bold' }}>All Total</span>
                                            <span className="orders-bill-value" style={{ fontWeight: 'bold' }}>{orderDetails.billDetails?.totalPrice} QR</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Add Product Modal */}
            {showAddProductModal && (
                <div className="orders-add-product-modal-overlay">
                    <div className="orders-add-product-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="orders-add-product-modal-header">
                            <h3>{productOptions ? 'Select Options' : 'Add Product'}</h3>
                            <button className="orders-close-button" onClick={() => resetAddProductModal()}>×</button>
                        </div>

                        <div className="orders-add-product-modal-body">
                            {!productOptions ? (
                                <>
                                    <div className="orders-add-product-form-group">
                                        <label>Product ID</label>
                                        <input
                                            type="number"
                                            placeholder="Enter Product ID"
                                            value={addProductForm.productId}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => setAddProductForm(prev => ({ ...prev, productId: e.target.value }))}
                                            className="orders-add-product-input"
                                        />
                                    </div>

                                    <div className="orders-add-product-form-group">
                                        <label>Quantity</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={addProductForm.qty}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => setAddProductForm(prev => ({ ...prev, qty: e.target.value }))}
                                            className="orders-add-product-input"
                                        />
                                    </div>

                                    <button
                                        className="orders-add-product-submit-btn"
                                        onClick={handleAddProductSubmit}
                                        disabled={addProductLoading}
                                    >
                                        {addProductLoading ? 'Adding...' : 'Add Product'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="orders-product-options-info">
                                        <div style={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                                            <strong>{productOptions.name}</strong>
                                            <span className="orders-product-options-total">
                                                {(selectedChoice?.price || selectedVariant?.price || productOptions.rate) * addProductForm.qty} QR
                                            </span>
                                        </div>
                                    </div>

                                    <div className="orders-add-product-form-group">
                                        <label>Quantity</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button
                                                className="orders-qty-btn"
                                                onClick={() => setAddProductForm(prev => ({ ...prev, qty: Math.max(1, parseInt(prev.qty) - 1) }))}
                                            >-</button>
                                            <span style={{ fontSize: '18px', fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>{addProductForm.qty}</span>
                                            <button
                                                className="orders-qty-btn"
                                                onClick={() => setAddProductForm(prev => ({ ...prev, qty: parseInt(prev.qty) + 1 }))}
                                            >+</button>
                                        </div>
                                    </div>

                                    {productOptions.choice && productOptions.choice.length > 0 && (
                                        <div className="orders-add-product-form-group">
                                            <label>Choice</label>
                                            <div className="orders-options-grid">
                                                {productOptions.choice.map((choice, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={`orders-option-btn ${selectedChoice?.title === choice.title ? 'orders-selected' : ''}`}
                                                        onClick={() => setSelectedChoice({ title: choice.title, price: choice.price })}
                                                    >
                                                        {choice.title} - {choice.price} QR
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {productOptions.variants && productOptions.variants.length > 0 && (
                                        <div className="orders-add-product-form-group">
                                            <label>Variant</label>
                                            <div className="orders-options-grid">
                                                {productOptions.variants.map((variant, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={`orders-option-btn ${selectedVariant?.sku === variant.sku ? 'orders-selected' : ''}`}
                                                        onClick={() => setSelectedVariant(variant)}
                                                    >
                                                        {variant.attributes.map(a => a.option_value).join(' / ')} - {variant.price} QR
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {productOptions.cleanAndCutType && productOptions.cleanAndCutType.length > 0 && (
                                        <div className="orders-add-product-form-group">
                                            <label>Clean & Cut Type</label>
                                            <div className="orders-options-grid">
                                                {productOptions.cleanAndCutType.map((type, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={`orders-option-btn ${selectedCleanType?.title === type.title ? 'orders-selected' : ''}`}
                                                        onClick={() => setSelectedCleanType({ title: type.title, price: type.price || 0 })}
                                                    >
                                                        {type.title}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        className="orders-add-product-submit-btn"
                                        onClick={handleAddProductWithOptions}
                                        disabled={addProductLoading}
                                    >
                                        {addProductLoading ? 'Adding...' : 'Add Product'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showEditItemModal && editingItem && (
                <div className="orders-add-product-modal-overlay">
                    <div className="orders-add-product-modal" onClick={e => e.stopPropagation()}>

                        <div className="orders-add-product-modal-header">
                            <h3>Edit Item</h3>
                            <button
                                className="orders-close-button"
                                onClick={() => setShowEditItemModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="orders-add-product-modal-body">
                            <div className="orders-product-options-info">
                                <strong>{editingItem.name}</strong>
                                <span className="orders-product-options-rate">
                                    {(editingItem.price || 0) * editItemQty} QR
                                </span>
                            </div>

                            <div className="orders-add-product-form-group">
                                <label>Quantity</label>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <button
                                        className="orders-qty-btn"
                                        onClick={() =>
                                            setEditItemQty(prev => Math.max(1, prev - 1))
                                        }
                                    >
                                        -
                                    </button>

                                    <span style={{ fontSize: 18, fontWeight: 700 }}>
                                        {editItemQty}
                                    </span>

                                    <button
                                        className="orders-qty-btn"
                                        onClick={() =>
                                            setEditItemQty(prev => prev + 1)
                                        }
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                                <button
                                    className="orders-add-product-submit-btn"
                                    disabled={editItemLoading}
                                    onClick={handleUpdateItem}
                                >
                                    Update Quantity
                                </button>

                                <button
                                    className="orders-add-product-submit-btn orders-delete-item-btn"
                                    onClick={openDeleteDialog}
                                    disabled={editItemLoading}
                                >
                                    Delete Item
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDialog && (
                <div className="orders-dialog-overlay">
                    <div className="orders-dialog">
                        <div className="orders-dialog-header">
                            <h2>Confirm Delete</h2>
                            <button
                                className="orders-dialog-close"
                                onClick={cancelDelete}
                            >
                                ×
                            </button>
                        </div>

                        <div className="orders-dialog-content">
                            <p>
                                Are you sure you want to delete
                                <strong> {editingItem?.name}</strong> from this order?
                                <br />
                                This action cannot be undone.
                            </p>
                        </div>

                        <div className="orders-dialog-footer">
                            <button
                                className="orders-cancel-btn"
                                onClick={cancelDelete}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>

                            <button
                                className="orders-delete-confirm-btn"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showStaffSelectModal && (
                <div className="staff-selection-overlay" onClick={closeStaffSelectModal}>
                    <div className="staff-selection-modal" onClick={e => e.stopPropagation()}>
                        <div className="staff-selection-header">
                            <h3>
                                {staffModalMode === 'share'
                                    ? 'Share via WhatsApp'
                                    : `Select ${staffAssignmentType === 'packing' ? 'Packing' : 'Delivery'} Staff`}
                            </h3>
                            <button className="staff-selection-close" onClick={closeStaffSelectModal}>×</button>
                        </div>
                        <div className="staff-selection-body">
                            {getStaffModalDrivers().length === 0 && staffModalMode !== 'share' ? (
                                <p className="staff-selection-empty">No active staff found.</p>
                            ) : (
                                <div className="staff-selection-grid">
                                    {getStaffModalDrivers().map((staff) => {
                                        const staffId = staff.id;
                                        const isSelected = staffModalMode !== 'share' && orderDetails.staff?.[staffAssignmentType]?.id === staffId;
                                            return (
                                                <div
                                                    key={staffId}
                                                    className={`staff-selection-item ${isSelected ? 'active' : ''} ${staffActionLoading ? 'disabled' : ''}`}
                                                    onClick={() => {
                                                        if (staffActionLoading) return;
                                                        if (staffModalMode === 'share') {
                                                            handleShareToStaff(staff);
                                                        } else {
                                                            handleAssignStaff(staffId);
                                                        }
                                                    }}
                                                    style={{
                                                        pointerEvents: staffActionLoading ? 'none' : 'auto',
                                                        opacity: staffActionLoading ? 0.7 : 1,
                                                        cursor: staffActionLoading ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    <span className="staff-selection-name">{staff.name}</span>
                                                    {staffActionLoading && staffModalMode !== 'share' && (
                                                        <div className="staff-selection-loader"></div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    {staffModalMode === 'share' && (
                                        <div
                                            className="staff-selection-item"
                                            onClick={handleShareToWhatsAppOther}
                                        >
                                            <span className="staff-selection-name">Other</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showUserModal && (
                <div className="orders-modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="orders-modal-content orders-user-detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="orders-modal-header">
                            <h2>User Details</h2>
                            <button className="orders-close-button" onClick={() => setShowUserModal(false)}>×</button>
                        </div>
                        <div className="orders-modal-body-scrollable">
                            <Users userFilter={selectedUserFilter} isModal={true} />
                        </div>
                    </div>
                </div>
            )}

             {showAddressModal && (
                <div className="orders-modal-overlay orders-address-select-overlay" onClick={() => setShowAddressModal(false)} style={{ zIndex: 1200 }}>
                    <div className="orders-modal-content orders-address-select-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="orders-modal-header">
                            <h2>Select Customer Address</h2>
                            <button className="orders-close-button" onClick={() => setShowAddressModal(false)}>×</button>
                        </div>
                        <div className="orders-modal-body-scrollable">
                            <div className="orders-address-select-list">
                                {customerAddresses.map((address, idx) => {
                                    const isCurrent = isSameAddress(address, orderDetails?.address);
                                    return (
                                        <div 
                                            key={address._id || idx} 
                                            className={`orders-address-select-item ${isCurrent ? 'current' : ''}`}
                                        >
                                            <div className="orders-address-select-details">
                                                {isCurrent && (
                                                    <span className="orders-address-current-badge">
                                                        Current Address
                                                    </span>
                                                )}
                                                <div className="orders-address-field-row">
                                                    <strong>Type:</strong> <span className="orders-address-badge">{address.type || 'N/A'}</span>
                                                </div>
                                                {address.receiversName && (
                                                    <div className="orders-address-field-row">
                                                        <strong>Name:</strong> {address.receiversName}
                                                    </div>
                                                )}
                                                {(address.receiversPhoneNUmber || address.receiversPhoneNumber || address.receiversPhone) && (
                                                    <div className="orders-address-field-row">
                                                        <strong>Phone:</strong> {address.receiversPhoneNUmber || address.receiversPhoneNumber || address.receiversPhone}
                                                    </div>
                                                )}
                                                {address.receiversWhatsappNumber && (
                                                    <div className="orders-address-field-row">
                                                        <strong>WhatsApp:</strong> {address.receiversWhatsappNumber}
                                                    </div>
                                                )}
                                                <div className="orders-address-field-row">
                                                    <strong>Zone:</strong> {address.zone || 'N/A'}
                                                </div>
                                                {(address.houseNo || address.apartment) && (
                                                    <div className="orders-address-field-row">
                                                        <strong>House/Apartment:</strong> {address.houseNo || address.apartment}
                                                    </div>
                                                )}
                                                {address.directionToReach && (
                                                    <div className="orders-address-field-row">
                                                        <strong>Directions:</strong> {address.directionToReach}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="orders-address-select-action">
                                                <button 
                                                    className={`orders-modal-button ${isCurrent ? 'secondary' : 'primary'}`}
                                                    onClick={() => handleSelectAddress(address)}
                                                    disabled={modalLoading}
                                                >
                                                    Select
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDispatchEditModal && selectedRoute && (
                <DispatchEditModal
                    isOpen={showDispatchEditModal}
                    onClose={() => setShowDispatchEditModal(false)}
                    route={selectedRoute}
                    renderHistorySlot={false}
                    drivers={dispatchDrivers}
                    storeCoordinates={dispatchStoreCoords}
                    onSave={async (updatedRoute) => {
                        try {
                            const routeId = updatedRoute._id || updatedRoute.id || selectedRoute._id || selectedRoute.id;
                            const response = await dispatchAPI.updateRouteInHistory(
                                routeId,
                                updatedRoute.plans,
                                updatedRoute.slot,
                                updatedRoute.date,
                                updatedRoute.slotId
                            );

                            if (response.success) {
                                toast.success("Dispatch route updated successfully!");
                                fetchOrders();
                                if (selectedOrderId) {
                                    fetchOrderDetails(selectedOrderId);
                                }
                                setShowDispatchEditModal(false);
                                setSelectedOrderId(null);
                                return true;
                            } else {
                                toast.error(response.msg || "Failed to update dispatch route");
                                return false;
                            }
                        } catch (error) {
                            console.error("Error saving dispatch route:", error);
                            toast.error("An error occurred while saving");
                            return false;
                        }
                    }}
                />
            )}
            {/* Status Change Confirmation Modal */}
            {statusConfirm.isOpen && (
                <div className="orders-modal-overlay orders-confirm-overlay" onClick={() => !modalLoading && setStatusConfirm({ ...statusConfirm, isOpen: false })}>
                    <div className="orders-modal-content orders-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="orders-confirm-header">
                            <div className="orders-confirm-header-title">
                                {statusConfirm.icon && <statusConfirm.icon size={20} />}
                                <h3>{statusConfirm.title}</h3>
                            </div>
                            <button
                                className="orders-confirm-close-btn"
                                onClick={() => setStatusConfirm({ ...statusConfirm, isOpen: false })}
                                disabled={modalLoading}
                            >
                                ×
                            </button>
                        </div>
                        <div className="orders-confirm-body">
                            <p>{statusConfirm.message}</p>

                            {statusConfirm.status === ORDER_STATUS.CANCELLED && (
                                <div className="orders-confirm-input-group">
                                    <label>Reason for cancellation (Optional)</label>
                                    <textarea
                                        placeholder="Enter reason for cancellation..."
                                        value={cancelReasonInput}
                                        onChange={(e) => setCancelReasonInput(e.target.value)}
                                        className="orders-confirm-textarea"
                                        disabled={modalLoading}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="orders-confirm-actions">
                            <button
                                className="orders-confirm-btn cancel"
                                onClick={() => setStatusConfirm({ ...statusConfirm, isOpen: false })}
                                disabled={modalLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className={`orders-confirm-btn confirm ${statusConfirm.color}`}
                                onClick={() => handleUpdateOrderStatus(statusConfirm.status, cancelReasonInput, true)}
                                disabled={modalLoading}
                            >
                                {modalLoading ? "Processing..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Search Modal */}
            {showSearchModal && (
                <div className="orders-modal-overlay">
                    <div className="orders-modal-content orders-global-search-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="orders-modal-header">Find Order</h2>

                        <div className="orders-form-group">
                            <label className="orders-form-label">OrderId</label>
                            <input
                                type="text"
                                className="orders-modal-input global-search-input"
                                placeholder="Enter Order ID..."
                                value={modalSearchQuery}
                                onChange={(e) => setModalSearchQuery(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleGlobalSearch();
                                }}
                            />
                        </div>

                        <div className="orders-modal-actions">
                            <button
                                className="orders-modal-button secondary"
                                onClick={() => setShowSearchModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="orders-modal-button primary"
                                onClick={handleGlobalSearch}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Coupon Details Modal */}
            {isCouponDetailsOpen && selectedCoupon && (
                <div className="orders-coupon-dialog-overlay" onClick={() => setIsCouponDetailsOpen(false)}>
                    <div className="orders-coupon-dialog-box" onClick={e => e.stopPropagation()}>
                        <div className="orders-coupon-dialog-header">
                            <h2>Coupon Details</h2>
                            <button className="orders-coupon-close-btn" onClick={() => setIsCouponDetailsOpen(false)}>
                                &times;
                            </button>
                        </div>

                        <div className="orders-coupon-details-content">
                            <div className="orders-coupon-detail-grid">
                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Coupon Name</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.couponName}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Discount</span>
                                    <span className="orders-coupon-detail-value">{getDiscountDisplay(selectedCoupon)}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">User Type</span>
                                    <span className="orders-coupon-detail-value">{getUserTypeDisplay(selectedCoupon.userType, selectedCoupon.userCount)}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Status</span>
                                    <span className="orders-coupon-detail-value" style={{ textTransform: "uppercase" }}>{selectedCoupon.status || '-'}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Visibility</span>
                                    <span className="orders-coupon-detail-value">
                                        {selectedCoupon.isPublic ? 'Public' : 'Hidden'}
                                    </span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Start Date</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.startDate || '-'}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">End Date</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.endDate || '-'}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Min Order</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.minOrderAmount ? `QR ${selectedCoupon.minOrderAmount}` : '0'}</span>
                                </div>

                                {!IS_ALFAJR && selectedCoupon.discountType === 'percentage_free_delivery' && selectedCoupon.minorderforDelivery > 0 && (
                                    <div className="orders-coupon-detail-item">
                                        <span className="orders-coupon-detail-label">Min Order (Delivery)</span>
                                        <span className="orders-coupon-detail-value">QR {selectedCoupon.minorderforDelivery}</span>
                                    </div>
                                )}

                                {(selectedCoupon.discountType === 'percentage' || selectedCoupon.discountType === 'percentage_free_delivery') && (
                                    <div className="orders-coupon-detail-item">
                                        <span className="orders-coupon-detail-label">Max Discount</span>
                                        <span className="orders-coupon-detail-value">{selectedCoupon.maxDiscountAmount ? `QR ${selectedCoupon.maxDiscountAmount}` : '0'}</span>
                                    </div>
                                )}

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Usage</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.usageType === 'unlimited' ? 'Unlimited' : `Limited: ${selectedCoupon.usageLimit}`}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Application Type</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.applicationType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                </div>

                                {(selectedCoupon.applicationType === 'selected_categories' || selectedCoupon.applicationType === 'selected_products') && (
                                    <div className="orders-coupon-detail-item">
                                        <span className="orders-coupon-detail-label">Application Mode</span>
                                        <span className="orders-coupon-detail-value">{(selectedCoupon.applicationMode || "include").replace(/\b\w/g, l => l.toUpperCase())}</span>
                                    </div>
                                )}

                                {selectedCoupon.firstOrderOnlyCount > 0 && (
                                    <div className="orders-coupon-detail-item">
                                        <span className="orders-coupon-detail-label">First X Orders Only</span>
                                        <span className="orders-coupon-detail-value">
                                            First {selectedCoupon.firstOrderOnlyCount} orders
                                        </span>
                                    </div>
                                )}

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Send Automated Notifications</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.sendNotifications ? 'Enabled' : 'Disabled'}</span>
                                </div>

                                <div className="orders-coupon-detail-item">
                                    <span className="orders-coupon-detail-label">Created At</span>
                                    <span className="orders-coupon-detail-value">{selectedCoupon.createdAt || '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="orders-coupon-dialog-footer">
                            <button
                                className="orders-coupon-analytics-btn"
                                onClick={() => handleOpenAnalytics(selectedCoupon._id)}
                            >
                                Analytics
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isAnalyticsDialogOpen && (
                <div className="orders-coupon-dialog-overlay" onClick={() => {
                    setIsAnalyticsDialogOpen(false);
                    setAnalyticsData(null);
                }}>
                    <div className="orders-coupon-dialog-box orders-coupon-analytics-dialog" onClick={e => e.stopPropagation()}>
                        <div className="orders-coupon-dialog-header">
                            <h2>Coupon Analytics</h2>
                            <button className="orders-coupon-close-btn" onClick={() => {
                                setIsAnalyticsDialogOpen(false);
                                setAnalyticsData(null);
                            }}>
                                &times;
                            </button>
                        </div>

                        <div className="orders-coupon-details-content">
                            {analyticsLoading ? (
                                <div className="orders-coupon-loading-state">
                                    <div className="orders-coupon-loading-spinner"></div>
                                    <p>Loading analytics...</p>
                                </div>
                            ) : analyticsData ? (
                                <>
                                    <h3 className="orders-coupon-analytics-title">{analyticsData.couponName}</h3>
                                    <div className="orders-coupon-analytics-grid">
                                        <div className="orders-coupon-analytics-card">
                                            <span className="orders-coupon-analytics-card-value">{analyticsData.orderCount}</span>
                                            <span className="orders-coupon-analytics-card-label">Orders</span>
                                        </div>
                                        <div className="orders-coupon-analytics-card">
                                            <span className="orders-coupon-analytics-card-value">{analyticsData.userCount}</span>
                                            <span className="orders-coupon-analytics-card-label">Users</span>
                                        </div>
                                        <div className="orders-coupon-analytics-card">
                                            <span className="orders-coupon-analytics-card-value">QR {analyticsData.totalDiscount}</span>
                                            <span className="orders-coupon-analytics-card-label">Discount</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="orders-coupon-empty-state">
                                    <p>No analytics data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {(customOrderOverlayId || showCustomCreateModal) && (
                <CustomOrderModals
                    adminData={adminData}
                    drivers={filterOptions.drivers}
                    overlayOrderId={customOrderOverlayId}
                    showCreateModal={showCustomCreateModal}
                    onOverlayClose={closeCustomOrderOverlay}
                    onOrderChange={() => fetchOrders(pagination.currentPage)}
                    onEditInDispatch={handleCustomOrderEditInDispatch}
                    dispatchLoading={dispatchLoading}
                />
            )}

            {showDailyOvertimeModal && (
                <div className="orders-modal-overlay" onClick={() => setShowDailyOvertimeModal(false)} style={{ zIndex: 1200 }}>
                    <div className="orders-modal-content daily-overtime-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="orders-modal-header daily-overtime-modal-header">
                            <h2>Daily Overtime</h2>
                            <button className="orders-close-button daily-overtime-modal-close-btn" onClick={() => setShowDailyOvertimeModal(false)}>×</button>
                        </div>
                        <div className="orders-modal-body daily-overtime-modal-body">
                            {(!stats?.dailyOvertime || stats.dailyOvertime.length === 0) ? (
                                <div className="daily-overtime-empty">
                                    No overtime recorded for this period.
                                </div>
                            ) : (
                                <div className="daily-overtime-list">
                                    {stats.dailyOvertime.map((dayData, index) => (
                                        <div key={index} className="daily-overtime-item">
                                            <span className="daily-overtime-item-date">
                                                {dayData._id}
                                            </span>
                                            <span className="daily-overtime-item-value">
                                                {dayData.overtimeOrdersCount || 0} {dayData.overtimeOrdersCount === 1 ? 'Order' : 'Orders'} | {formatAmount(dayData.totalOvertime || 0)} QR
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default Orders;